import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, integer, boolean, pgEnum, jsonb, uniqueIndex, index, numeric, serial, bigserial, primaryKey } from "drizzle-orm/pg-core";
import type { AgentStatusHistoryEvent } from "./agent-status-history-event";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import type {
  RecordingCaptureHandle,
  RecordingPauseInterval,
  RecordingPolicySnapshot,
} from "./recording-contracts";
import type {
  IntegrationCategory,
  IntegrationConnectionStatus,
  IntegrationOperationProgress,
  IntegrationOperationStatus,
  IntegrationOperationTrigger,
  IntegrationOperationType,
  IntegrationProviderKey,
} from "./integration-hub";

export const userRoleEnum = pgEnum("user_role", ["super_admin", "tenant_admin", "supervisor", "agent"]);
export const tenantStatusEnum = pgEnum("tenant_status", ["active", "suspended", "trial"]);

export const tenants = pgTable("tenants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  domain: text("domain"),
  contactName: text("contact_name"),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  status: tenantStatusEnum("status").notNull().default("trial"),
  billingEmail: text("billing_email"),
  billingPlan: text("billing_plan").default("trial"),
  features: jsonb("features").default({ recordingPipelineV2: true }),
  maxAgents: integer("max_agents").default(5),
  maxPhoneNumbers: integer("max_phone_numbers").default(2),
  defaultCountryCode: text("default_country_code").default("+1"),
  duplicateHandling: text("duplicate_handling").default("update"),
  voiceAiEnabled: boolean("voice_ai_enabled").default(false),
  soundNotificationSettings: jsonb("sound_notification_settings").$type<{
    conversationEnabled: boolean;
    conversationVolume: number;
    conversationAudioUrl?: string | null;
    queueEnabled: boolean;
    queueVolume: number;
    queueSoundRoles: string[];
    queueIds: string[];
    queueAudioUrl?: string | null;
  }>().default({
    conversationEnabled: true,
    conversationVolume: 100,
    conversationAudioUrl: null,
    queueEnabled: false,
    queueVolume: 100,
    queueSoundRoles: ["tenant_admin", "supervisor"],
    queueIds: [],
    queueAudioUrl: null,
  }),
  dispositionConversionConfig: jsonb("disposition_conversion_config").$type<{
    outbound: Array<{ id: string; label: string; dispositions: string[] }>;
    inbound:  Array<{ id: string; label: string; dispositions: string[] }>;
  }>().default({ outbound: [], inbound: [] }),
  billingVisibleUserIds: text("billing_visible_user_ids").array(),
  createdAt: timestamp("created_at").defaultNow(),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
  purgeAfter: timestamp("purge_after", { withTimezone: true }),
  purgeRetryAt: timestamp("purge_retry_at", { withTimezone: true }),
}, (table) => [
  index("idx_tenants_retention_due").on(table.purgeAfter, table.purgeRetryAt)
    .where(sql`${table.deletedAt} IS NOT NULL`),
]);

export const insertTenantSchema = createInsertSchema(tenants).omit({
  id: true,
  createdAt: true,
  deletedAt: true,
  purgeAfter: true,
  purgeRetryAt: true,
});

export type InsertTenant = z.infer<typeof insertTenantSchema>;
export type Tenant = typeof tenants.$inferSelect;

// Platform approval receipts survive removal of the tenant's business data.
export const tenantPurgeRequests = pgTable("tenant_purge_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  requestedBy: varchar("requested_by").notNull(),
  requestedAt: timestamp("requested_at", { withTimezone: true }).notNull().defaultNow(),
  reason: text("reason").notNull(),
  status: text("status").notNull().default("queued").$type<"queued" | "processing" | "failed" | "completed">(),
  completedAt: timestamp("completed_at", { withTimezone: true }),
  lastError: text("last_error"),
}, (table) => [index("idx_tenant_purge_requests_tenant").on(table.tenantId, table.requestedAt)]);
export type TenantPurgeRequest = typeof tenantPurgeRequests.$inferSelect;
export type ArchivedTenant = Tenant & { purgeRequest: TenantPurgeRequest | null };

// Private agreement keys are never included in the general tenant API response.
export const tenantSubscriptionAgreements = pgTable("tenant_subscription_agreements", {
  tenantId: varchar("tenant_id").primaryKey().references(() => tenants.id, { onDelete: "cascade" }),
  objectKey: text("object_key").notNull(),
  filename: text("filename").notNull(),
  uploadedAt: timestamp("uploaded_at").notNull().defaultNow(),
});

// Deleted-tenant agreements remain identifiable until their retention expires.
export const tenantAgreementObjectCleanup = pgTable("tenant_agreement_object_cleanup", {
  objectKey: text("object_key").primaryKey(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  deleteAfter: timestamp("delete_after", { withTimezone: true }).notNull().defaultNow(),
  tenantId: varchar("tenant_id"),
  tenantName: text("tenant_name"),
  filename: text("filename"),
  uploadedAt: timestamp("uploaded_at"),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
}, (table) => [
  index("idx_tenant_agreement_cleanup_delete_after").on(table.deleteAfter, table.objectKey),
  index("idx_tenant_agreement_cleanup_tenant_id").on(table.tenantId),
]);

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull(),
  password: text("password").notNull(),
  displayName: text("display_name").notNull(),
  role: userRoleEnum("role").notNull().default("agent"),
  tenantId: varchar("tenant_id"),
  plivoEndpointId: varchar("plivo_endpoint_id"),
  isActive: boolean("is_active").default(true),
  lastLoginAt: timestamp("last_login_at"),
  resetToken: varchar("reset_token"),
  resetTokenExpiresAt: timestamp("reset_token_expires_at"),
  twoFactorCode: varchar("two_factor_code"),
  twoFactorExpiresAt: timestamp("two_factor_expires_at"),
  twoFactorEnabled: boolean("two_factor_enabled").default(true),
  isAiBot: boolean("is_ai_bot").default(false),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("users_email_tenant_idx").on(table.email, table.tenantId),
]);

export const authRateLimits = pgTable("auth_rate_limits", {
  key: text("key").primaryKey(),
  attempts: integer("attempts").notNull(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
}, (table) => [index("auth_rate_limits_expires_idx").on(table.expiresAt)]);

export const trustedDevices = pgTable("trusted_devices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  deviceToken: varchar("device_token").notNull(),
  userAgent: text("user_agent"),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  plivoEndpointId: true,
  lastLoginAt: true,
  resetToken: true,
  resetTokenExpiresAt: true,
  twoFactorCode: true,
  twoFactorExpiresAt: true,
  createdAt: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

export const callDirectionEnum = pgEnum("call_direction", ["inbound", "outbound"]);
export const callStatusEnum = pgEnum("call_status", ["ringing", "in-progress", "completed", "missed", "failed", "busy", "no-answer", "queued", "abandoned", "ivr_exit", "rejected", "blocked", "overflow", "voicemail", "answered", "callback_requested"]);

export const contacts = pgTable("contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  phoneNumber: text("phone_number").notNull(),
  email: text("email"),
  company: text("company"),
  notes: text("notes"),
  favorite: boolean("favorite").default(false),
  lastDisposition: text("last_disposition"),
  lastDispositionNote: text("last_disposition_note"),
  lastDispositionSource: text("last_disposition_source"),
  lastDispositionAt: timestamp("last_disposition_at"),
  jobTitle: text("job_title"),
  industry: text("industry"),
  website: text("website"),
  address: text("address"),
  language: text("language"),
  fyi: text("fyi"),
  source: text("source"),
  doNotCall: boolean("do_not_call").default(false),
  doNotSms: boolean("do_not_sms").default(false),
  doNotEmail: boolean("do_not_email").default(false),
  blockedInbound: boolean("blocked_inbound").default(false),
  tenantContactNumber: integer("tenant_contact_number"),
  facebookId: text("facebook_id"),
  instagramId: text("instagram_id"),
  importLogId: varchar("import_log_id"),
  externalLeadId: text("external_lead_id"),
  leadListName: text("lead_list_name"),
  adId: text("ad_id"),
  sourceCampaignId: text("source_campaign_id"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_contacts_tenant_id").on(table.tenantId),
  index("idx_contacts_tenant_phone").on(table.tenantId, table.phoneNumber),
  index("idx_contacts_dnc_all_tenant_name").on(table.tenantId, table.name, table.id)
    .where(sql`${table.doNotCall} OR ${table.doNotSms} OR ${table.doNotEmail}`),
  index("idx_contacts_dnc_call_tenant_name").on(table.tenantId, table.name, table.id)
    .where(sql`${table.doNotCall}`),
  index("idx_contacts_dnc_sms_tenant_name").on(table.tenantId, table.name, table.id)
    .where(sql`${table.doNotSms}`),
  index("idx_contacts_dnc_email_tenant_name").on(table.tenantId, table.name, table.id)
    .where(sql`${table.doNotEmail}`),
  index("idx_contacts_tenant_phone_normalized").on(
    table.tenantId,
    sql`regexp_replace(${table.phoneNumber}, '[^0-9]', '', 'g')`,
  ),
  index("idx_contacts_tenant_contact_number_cursor").on(
    table.tenantId,
    table.tenantContactNumber,
    table.id,
  ),
  index("idx_contacts_tenant_name_cursor").on(table.tenantId, table.name, table.id),
]);

export const insertContactSchema = createInsertSchema(contacts).omit({
  id: true,
  lastDisposition: true,
  createdAt: true,
});

export type InsertContact = z.infer<typeof insertContactSchema>;
export type Contact = typeof contacts.$inferSelect;

export const contactPhones = pgTable("contact_phones", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  number: text("number").notNull(),
  label: text("label"),
  isPrimary: boolean("is_primary").default(false),
}, (table) => [
  index("idx_contact_phones_contact_id").on(table.contactId),
  index("idx_contact_phones_tenant_number").on(table.tenantId, table.number),
]);

export const insertContactPhoneSchema = createInsertSchema(contactPhones).omit({
  id: true,
});

export type InsertContactPhone = z.infer<typeof insertContactPhoneSchema>;
export type ContactPhone = typeof contactPhones.$inferSelect;

export const contactEmails = pgTable("contact_emails", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  email: text("email").notNull(),
  label: text("label"),
  isPrimary: boolean("is_primary").default(false),
}, (table) => [
  index("idx_contact_emails_contact_id").on(table.contactId),
]);

export const insertContactEmailSchema = createInsertSchema(contactEmails).omit({
  id: true,
});

export type InsertContactEmail = z.infer<typeof insertContactEmailSchema>;
export type ContactEmail = typeof contactEmails.$inferSelect;

export const blockedNumbers = pgTable("blocked_numbers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  phoneNumber: text("phone_number").notNull(),
  reason: text("reason"),
  contactId: varchar("contact_id"),
  contactName: text("contact_name"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("blocked_numbers_tenant_id_phone_number_key").on(table.tenantId, table.phoneNumber),
]);

export const insertBlockedNumberSchema = createInsertSchema(blockedNumbers).omit({
  id: true,
  createdAt: true,
});

export type InsertBlockedNumber = z.infer<typeof insertBlockedNumberSchema>;
export type BlockedNumber = typeof blockedNumbers.$inferSelect;

export const tags = pgTable("tags", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("tags_tenant_name_idx").on(table.tenantId, table.name),
]);

export const insertTagSchema = createInsertSchema(tags).omit({
  id: true,
  createdAt: true,
});

export type InsertTag = z.infer<typeof insertTagSchema>;
export type Tag = typeof tags.$inferSelect;

export const contactTags = pgTable("contact_tags", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  tagId: varchar("tag_id").notNull(),
}, (table) => [
  uniqueIndex("contact_tags_unique_idx").on(table.contactId, table.tagId),
]);

export const insertContactTagSchema = createInsertSchema(contactTags).omit({
  id: true,
});

export type InsertContactTag = z.infer<typeof insertContactTagSchema>;
export type ContactTag = typeof contactTags.$inferSelect;

export const agentStatusEnum = pgEnum("agent_status", ["ready", "not_ready", "on_call", "after_call_work", "offline", "ringing"]);

export const agentStatuses = pgTable("agent_statuses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  endpointId: varchar("endpoint_id").notNull(),
  status: agentStatusEnum("status").notNull().default("offline"),
  customLabel: text("custom_label"),
  preCallStatus: text("pre_call_status"),
  preCallCustomLabel: text("pre_call_custom_label"),
  updatedAt: timestamp("updated_at").defaultNow(),
  // SIP resilience columns (added in Task #440)
  currentCallLogId: text("current_call_log_id"),
  currentProviderCallUuid: text("current_provider_call_uuid"),
  statusVersion: integer("status_version").notNull().default(0),
  sipUnavailableEpisodeId: text("sip_unavailable_episode_id"),
  changeSource: text("change_source"),
}, (table) => [
  index("idx_agent_statuses_endpoint_id").on(table.endpointId),
  index("idx_agent_statuses_tenant_id").on(table.tenantId),
]);

export const insertAgentStatusSchema = createInsertSchema(agentStatuses).omit({
  id: true,
  updatedAt: true,
});

export type InsertAgentStatus = z.infer<typeof insertAgentStatusSchema>;
export type AgentStatus = typeof agentStatuses.$inferSelect;

export const agentStatusHistory = pgTable("agent_status_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  endpointId: varchar("endpoint_id").notNull(),
  userId: varchar("user_id"),
  previousStatus: text("previous_status"),
  previousCustomLabel: text("previous_custom_label"),
  newStatus: text("new_status").notNull(),
  newCustomLabel: text("new_custom_label"),
  changedAt: timestamp("changed_at").defaultNow(),
  durationSeconds: integer("duration_seconds"),
  changedBy: text("changed_by"),
  changeSource: text("change_source"),
  callUuid: text("call_uuid"),
});

export const insertAgentStatusHistorySchema = createInsertSchema(agentStatusHistory).omit({
  id: true,
  changedAt: true,
});

export type InsertAgentStatusHistory = z.infer<typeof insertAgentStatusHistorySchema>;
export type AgentStatusHistory = typeof agentStatusHistory.$inferSelect;

export const agentStatusHistoryOutbox = pgTable("agent_status_history_outbox", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  endpointId: varchar("endpoint_id").notNull(),
  sequence: bigserial("sequence", { mode: "number" }).notNull(),
  payload: jsonb("payload").$type<AgentStatusHistoryEvent>().notNull(),
  attempts: integer("attempts").notNull().default(0),
  nextAttemptAt: timestamp("next_attempt_at", { withTimezone: true }).notNull().defaultNow(),
  lastError: text("last_error"),
}, table => [
  index("agent_status_history_outbox_tenant_idx").on(table.tenantId),
  index("agent_status_history_outbox_agent_sequence_idx").on(table.tenantId, table.endpointId, table.sequence),
  index("agent_status_history_outbox_due_idx").on(table.nextAttemptAt, table.sequence),
]);

export const callRejections = pgTable("call_rejections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  callUuid: text("call_uuid"),
  callLogId: varchar("call_log_id"),
  // Actual provider UUID for the agent leg. Queue rows share a caller parent,
  // so this is the identity used to deduplicate one physical ring attempt.
  providerAttemptUuid: text("provider_attempt_uuid"),
  agentEndpointId: varchar("agent_endpoint_id").notNull(),
  direction: text("direction").default("inbound"),
  reason: text("reason"), // "declined" = agent clicked reject; "missed" = timeout/no-answer
  rejectedAt: timestamp("rejected_at").defaultNow(),
});

export const insertCallRejectionSchema = createInsertSchema(callRejections).omit({
  id: true,
  rejectedAt: true,
});

export type InsertCallRejection = z.infer<typeof insertCallRejectionSchema>;
export type CallRejection = typeof callRejections.$inferSelect;

export const ringStrategyEnum = pgEnum("ring_strategy", ["ring_all", "round_robin", "least_recent", "random"]);

export const callGroups = pgTable("call_groups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  ringStrategy: ringStrategyEnum("ring_strategy").notNull().default("ring_all"),
  ringTimeout: integer("ring_timeout").default(30),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCallGroupSchema = createInsertSchema(callGroups).omit({
  id: true,
  createdAt: true,
});

export type InsertCallGroup = z.infer<typeof insertCallGroupSchema>;
export type CallGroup = typeof callGroups.$inferSelect;

export const callGroupMembers = pgTable("call_group_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  groupId: varchar("group_id").notNull(),
  endpointId: varchar("endpoint_id").notNull(),
  priority: integer("priority").default(1),
}, (table) => [
  index("idx_call_group_members_group_id").on(table.groupId),
]);

export const insertCallGroupMemberSchema = createInsertSchema(callGroupMembers).omit({
  id: true,
});

export type InsertCallGroupMember = z.infer<typeof insertCallGroupMemberSchema>;
export type CallGroupMember = typeof callGroupMembers.$inferSelect;

export const queues = pgTable("queues", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  maxWaitTime: integer("max_wait_time").default(300),
  ringStrategy: ringStrategyEnum("ring_strategy_q").notNull().default("round_robin"),
  musicOnHold: text("music_on_hold"),
  announcePosition: boolean("announce_position").default(true),
  announceFrequency: integer("announce_frequency").default(30),
  announceEwt: boolean("announce_ewt").default(false),
  maxWaitMessage: text("max_wait_message"),
  maxWaitMessageType: text("max_wait_message_type").$type<"none" | "tts" | "audio">().default("none"),
  maxWaitMessageAudioUrl: text("max_wait_message_audio_url"),
  periodicAnnouncements: jsonb("periodic_announcements").$type<Array<{message: string, type: 'speak' | 'play', interval?: number, repeat?: 'once' | 'every'}>>(),
  overflowGroupId: varchar("overflow_group_id"),
  overflowAction: text("overflow_action").$type<"call_group" | "queue" | "ivr_menu" | "endpoint" | "external" | "voicemail" | "hangup">(),
  overflowTarget: text("overflow_target"),
  overflowVoicemailType: text("overflow_voicemail_type").$type<"default" | "tts" | "audio">(),
  overflowVoicemailGreeting: text("overflow_voicemail_greeting"),
  overflowVoicemailAudioUrl: text("overflow_voicemail_audio_url"),
  overflowVoicemailEmailRecipients: text("overflow_voicemail_email_recipients").array(),
  maxQueueSize: integer("max_queue_size"),
  callbackEnabled: boolean("callback_enabled").default(false),
  callbackKey: text("callback_key").default("1"),
  callbackMaxAttempts: integer("callback_max_attempts").default(3),
  callbackAnnounceEnabled: boolean("callback_announce_enabled").default(true),
  callbackAnnounceText: text("callback_announce_text"),
  callbackPreActionAudioUrl: text("callback_pre_action_audio_url"),
  missThreshold: integer("miss_threshold").default(2),
  welcomeMessageType: text("welcome_message_type").$type<"none" | "audio" | "tts">().default("none"),
  welcomeMessageAudio: text("welcome_message_audio"),
  welcomeMessageText: text("welcome_message_text"),
  skipWelcomeIfAgentAvailable: boolean("skip_welcome_if_agent_available").default(false),
  overrideBusinessHours: boolean("override_business_hours").default(false),
  afterHoursAction: text("after_hours_action").$type<"voicemail" | "forward" | "message" | "hangup">().default("voicemail"),
  afterHoursForwardNumber: text("after_hours_forward_number"),
  afterHoursMessage: text("after_hours_message"),
  voicemailGreetingType: text("voicemail_greeting_type").$type<"tts" | "audio">().default("tts"),
  voicemailGreeting: text("queue_voicemail_greeting"),
  voicemailGreetingAudioUrl: text("voicemail_greeting_audio_url"),
  voicemailEmailRecipients: text("voicemail_email_recipients").array(),
  scriptId: varchar("script_id"),
  noAgentOverflowEnabled: boolean("no_agent_overflow_enabled").default(false),
  noAgentOverflowAction: text("no_agent_overflow_action").$type<"call_group" | "queue" | "ivr_menu" | "endpoint" | "external" | "voicemail" | "hangup">(),
  noAgentOverflowTarget: text("no_agent_overflow_target"),
  noAgentOverflowVoicemailType: text("no_agent_overflow_voicemail_type").$type<"default" | "tts" | "audio">(),
  noAgentOverflowVoicemailGreeting: text("no_agent_overflow_voicemail_greeting"),
  noAgentOverflowVoicemailAudioUrl: text("no_agent_overflow_voicemail_audio_url"),
  noAgentOverflowVoicemailEmailRecipients: text("no_agent_overflow_voicemail_email_recipients").array(),
  queueFullOverflowAction: text("queue_full_overflow_action").$type<"call_group" | "queue" | "ivr_menu" | "endpoint" | "external" | "voicemail" | "hangup">(),
  queueFullOverflowTarget: text("queue_full_overflow_target"),
  queueFullOverflowVoicemailType: text("queue_full_overflow_voicemail_type").$type<"default" | "tts" | "audio">(),
  queueFullOverflowVoicemailGreeting: text("queue_full_overflow_voicemail_greeting"),
  queueFullOverflowVoicemailAudioUrl: text("queue_full_overflow_voicemail_audio_url"),
  queueFullOverflowVoicemailEmailRecipients: text("queue_full_overflow_voicemail_email_recipients").array(),
  queueFullOverflowAudioUrl: text("queue_full_overflow_audio_url"),
  callerInputEnabled: boolean("caller_input_enabled").default(false),
  callerInputKey: text("caller_input_key").default("2"),
  callerInputAction: text("caller_input_action").$type<"call_group" | "queue" | "ivr_menu" | "endpoint" | "external" | "voicemail" | "hangup">(),
  callerInputTarget: text("caller_input_target"),
  callerInputAudioUrl: text("caller_input_audio_url"),
  callerInputVoicemailType: text("caller_input_voicemail_type").$type<"default" | "tts" | "audio">(),
  callerInputVoicemailGreeting: text("caller_input_voicemail_greeting"),
  callerInputVoicemailAudioUrl: text("caller_input_voicemail_audio_url"),
  callerInputVoicemailEmailRecipients: text("caller_input_voicemail_email_recipients").array(),
  callerInputPrompt: text("caller_input_prompt"),
  afterHoursVoiceAiAgentId: varchar("after_hours_voice_ai_agent_id"),
  aiOverflowAgentId: varchar("ai_overflow_agent_id"),
  aiOverflowKey: text("ai_overflow_key").default("0"),
  aiOverflowEwtThreshold: integer("ai_overflow_ewt_threshold").default(60),
  queuePriority: integer("queue_priority").default(10),
  legacyId: text("legacy_id"),
  displayId: integer("display_id"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("queues_tenant_name_idx").on(table.tenantId, table.name),
]);

export const insertQueueSchema = createInsertSchema(queues).omit({
  id: true,
  createdAt: true,
});

export type InsertQueue = z.infer<typeof insertQueueSchema>;
export type Queue = typeof queues.$inferSelect;

export const queueBusinessHours = pgTable("queue_business_hours", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  queueId: varchar("queue_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  dayOfWeek: text("day_of_week").notNull().$type<"monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday">(),
  isOpen: boolean("is_open").notNull().default(true),
  openTime: text("open_time").notNull().default("09:00"),
  closeTime: text("close_time").notNull().default("17:00"),
  openTime2: text("open_time_2"),
  closeTime2: text("close_time_2"),
}, (table) => [
  uniqueIndex("queue_business_hours_queue_day_idx").on(table.queueId, table.dayOfWeek),
]);

export const insertQueueBusinessHoursSchema = createInsertSchema(queueBusinessHours).omit({
  id: true,
});
export type InsertQueueBusinessHours = z.infer<typeof insertQueueBusinessHoursSchema>;
export type QueueBusinessHours = typeof queueBusinessHours.$inferSelect;

export const queueMembers = pgTable("queue_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  queueId: varchar("queue_id").notNull(),
  endpointId: varchar("endpoint_id").notNull(),
  priority: integer("priority").default(1),
}, (table) => [
  index("idx_queue_members_queue_id").on(table.queueId),
]);

export const insertQueueMemberSchema = createInsertSchema(queueMembers).omit({
  id: true,
});

export type InsertQueueMember = z.infer<typeof insertQueueMemberSchema>;
export type QueueMember = typeof queueMembers.$inferSelect;

export const callbackRequests = pgTable("callback_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  queueId: varchar("queue_id").notNull(),
  callerNumber: text("caller_number").notNull(),
  status: text("status").$type<"pending" | "in_progress" | "completed" | "failed" | "expired">().notNull().default("pending"),
  attempts: integer("attempts").notNull().default(0),
  maxAttempts: integer("max_attempts").notNull().default(3),
  lastAttemptAt: timestamp("last_attempt_at"),
  originalCallUuid: text("original_call_uuid"),
  callbackCallUuid: text("callback_call_uuid"),
  source: text("source").$type<"queue" | "automation">().default("queue"),
  agentWhisperText: text("agent_whisper_text"),
  script: text("script"),
  contactId: varchar("contact_id"),
  fromNumber: text("from_number"),
  requestedAt: timestamp("requested_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  nextRetryAt: timestamp("next_retry_at"),
}, (table) => [
  index("idx_callback_requests_queue_status").on(table.queueId, table.status),
]);

export const insertCallbackRequestSchema = createInsertSchema(callbackRequests).omit({
  id: true,
  requestedAt: true,
  completedAt: true,
});
export type InsertCallbackRequest = z.infer<typeof insertCallbackRequestSchema>;
export type CallbackRequest = typeof callbackRequests.$inferSelect;

export const phoneNumbers = pgTable("phone_numbers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  number: text("number").notNull(),
  friendlyName: text("friendly_name"),
  callerName: text("caller_name"),
  region: text("region"),
  country: text("country").default("US"),
  plivoNumberId: text("plivo_number_id"),
  smsProvider: text("sms_provider").default("plivo"),
  didwwSmsTrunkUser: text("didww_sms_trunk_user"),
  didwwSmsTrunkPassword: text("didww_sms_trunk_password"),
  carrierSource: text("carrier_source").default("plivo"),
  active: boolean("active").default(true),
  voiceEnabled: boolean("voice_enabled").notNull().default(true),
  mmsEnabled: boolean("mms_enabled").default(false),
  smsEnabled: boolean("sms_enabled").default(true),
  smsInboundWebhookUrl: text("sms_inbound_webhook_url"),
  smsInboundVerifiedAt: timestamp("sms_inbound_verified_at", { withTimezone: true }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPhoneNumberSchema = createInsertSchema(phoneNumbers).omit({
  id: true,
  createdAt: true,
});

export type InsertPhoneNumber = z.infer<typeof insertPhoneNumberSchema>;
export type PhoneNumber = typeof phoneNumbers.$inferSelect;

export const routingTypeEnum = pgEnum("routing_type", ["call_group", "queue", "ivr", "endpoint", "voice_ai"]);

export const numberRouting = pgTable("number_routing", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  phoneNumberId: varchar("phone_number_id").notNull(),
  routingType: routingTypeEnum("routing_type").notNull(),
  targetId: varchar("target_id").notNull(),
  priority: integer("priority").default(1),
});

export const insertNumberRoutingSchema = createInsertSchema(numberRouting).omit({
  id: true,
});

export type InsertNumberRouting = z.infer<typeof insertNumberRoutingSchema>;
export type NumberRouting = typeof numberRouting.$inferSelect;

export const ivrMenus = pgTable("ivr_menus", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  greetingText: text("greeting_text"),
  greetingAudioUrl: text("greeting_audio_url"),
  timeout: integer("timeout").default(5),
  maxRetries: integer("max_retries").default(3),
  invalidInputText: text("invalid_input_text").default("Invalid option. Please try again."),
  timeoutText: text("timeout_text"),
  timeoutAudioUrl: text("timeout_audio_url"),
  overrideBusinessHours: boolean("override_business_hours").default(false),
  afterHoursAction: text("after_hours_action").$type<"voicemail" | "forward" | "message" | "hangup">().default("message"),
  afterHoursForwardNumber: text("after_hours_forward_number"),
  afterHoursMessage: text("after_hours_message").default("We are currently closed. Please call back during business hours."),
  voicemailGreetingType: text("voicemail_greeting_type").$type<"tts" | "audio">().default("tts"),
  voicemailGreeting: text("voicemail_greeting"),
  voicemailGreetingAudioUrl: text("voicemail_greeting_audio_url"),
  voicemailEmailRecipients: text("voicemail_email_recipients").array(),
  timeoutOverflowAction: text("timeout_overflow_action").$type<"call_group" | "queue" | "ivr_menu" | "endpoint" | "external" | "voicemail" | "hangup">(),
  timeoutOverflowTarget: text("timeout_overflow_target"),
  overflowVoicemailType: text("overflow_voicemail_type").$type<"default" | "tts" | "audio">(),
  overflowVoicemailGreeting: text("overflow_voicemail_greeting"),
  overflowVoicemailAudioUrl: text("overflow_voicemail_audio_url"),
  overflowVoicemailEmailRecipients: text("overflow_voicemail_email_recipients").array(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("ivr_menus_tenant_name_idx").on(table.tenantId, table.name),
]);

export const insertIvrMenuSchema = createInsertSchema(ivrMenus).omit({
  id: true,
  createdAt: true,
});

export type InsertIvrMenu = z.infer<typeof insertIvrMenuSchema>;
export type IvrMenu = typeof ivrMenus.$inferSelect;

export const ivrActionEnum = pgEnum("ivr_action", ["call_group", "queue", "endpoint", "ivr_menu", "voicemail", "hangup", "extension", "external_number"]);

export const ivrOptions = pgTable("ivr_options", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  menuId: varchar("menu_id").notNull(),
  digit: text("digit").notNull(),
  label: text("label"),
  actionType: ivrActionEnum("action_type").notNull(),
  targetId: varchar("target_id"),
});

export const insertIvrOptionSchema = createInsertSchema(ivrOptions).omit({
  id: true,
});

export type InsertIvrOption = z.infer<typeof insertIvrOptionSchema>;
export type IvrOption = typeof ivrOptions.$inferSelect;

export const ivrBusinessHours = pgTable("ivr_business_hours", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ivrMenuId: varchar("ivr_menu_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  dayOfWeek: text("day_of_week").notNull().$type<"monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday">(),
  isOpen: boolean("is_open").notNull().default(true),
  openTime: text("open_time").notNull().default("09:00"),
  closeTime: text("close_time").notNull().default("17:00"),
  openTime2: text("open_time_2"),
  closeTime2: text("close_time_2"),
}, (table) => [
  uniqueIndex("ivr_business_hours_menu_day_idx").on(table.ivrMenuId, table.dayOfWeek),
]);

export const insertIvrBusinessHoursSchema = createInsertSchema(ivrBusinessHours).omit({
  id: true,
});
export type InsertIvrBusinessHours = z.infer<typeof insertIvrBusinessHoursSchema>;
export type IvrBusinessHours = typeof ivrBusinessHours.$inferSelect;

export const contactNotes = pgTable("contact_notes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  callLogId: varchar("call_log_id"),
  createdBy: varchar("created_by"),
  createdByName: text("created_by_name"),
  title: text("title"),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertContactNoteSchema = createInsertSchema(contactNotes).omit({
  id: true,
  createdAt: true,
});

export type InsertContactNote = z.infer<typeof insertContactNoteSchema>;
export type ContactNote = typeof contactNotes.$inferSelect;

export const contactDispositionHistory = pgTable("contact_disposition_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  disposition: text("disposition").notNull(),
  note: text("note"),
  source: text("source"),
  agentName: text("agent_name"),
  changedAt: timestamp("changed_at").defaultNow(),
});

export type ContactDispositionHistory = typeof contactDispositionHistory.$inferSelect;

export const callLogs = pgTable("call_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  direction: callDirectionEnum("direction").notNull(),
  status: callStatusEnum("status").notNull(),
  fromNumber: text("from_number").notNull(),
  toNumber: text("to_number").notNull(),
  contactName: text("contact_name"),
  contactId: varchar("contact_id"),
  duration: integer("duration").default(0),
  callUuid: text("call_uuid"),
  agentCallUuid: text("agent_call_uuid"),
  recordingUrl: text("recording_url"),
  recordingSegments: jsonb("recording_segments"),
  disposition: text("disposition"),
  notes: text("notes"),
  callId: text("call_id"),
  agentId: varchar("agent_id"),
  agentEndpointId: varchar("agent_endpoint_id"),
  agentName: text("agent_name"),
  callGroupId: varchar("call_group_id"),
  queueId: varchar("queue_id"),
  ivrMenuId: varchar("ivr_menu_id"),
  parentIvrMenuId: varchar("parent_ivr_menu_id"),
  phoneNumberId: varchar("phone_number_id"),
  transferType: text("transfer_type"),
  transferredTo: text("transferred_to"),
  transferredBy: varchar("transferred_by"),
  parentCallId: varchar("parent_call_id"),
  isChildLeg: boolean("is_child_leg").default(false),
  callCost: numeric("call_cost", { precision: 12, scale: 4 }),
  costPerMinute: numeric("cost_per_minute", { precision: 12, scale: 6 }),
  tenantCallNumber: integer("tenant_call_number"),
  campaignId: varchar("campaign_id"),
  waitTime: integer("wait_time"),
  totalTalkTime: integer("total_talk_time"),
  acwTime: integer("acw_time"),
  acwStartedAt: timestamp("acw_started_at"),
  acwEndedAt: timestamp("acw_ended_at"),
  csatTransferredAt: timestamp("csat_transferred_at"),
  activityImportLogId: varchar("activity_import_log_id"),
  transferOutcome: text("transfer_outcome"),
  hangupCause: text("hangup_cause"),
  hangupSource: text("hangup_source"),
  callerRegion: text("caller_region"),
  startedAt: timestamp("started_at").defaultNow(),
  answeredAt: timestamp("answered_at"),
  endedAt: timestamp("ended_at"),
  /** How many raw (non-stitched) segments existed at the time of the last successful stitch. Used for late-segment detection. */
  stitchedSegmentCount: integer("stitched_segment_count"),
  /**
   * Incremented atomically (inside a SELECT … FOR UPDATE transaction) whenever a
   * genuinely new call_log_recordings row is inserted for this call.
   * Stitch workers snapshot this value at claim time and verify it is unchanged
   * before publishing — a mismatch means a late segment arrived and the result
   * must be discarded and rescheduled.  Only meaningful when
   * RECORDING_HARDENING_ENABLED=true.
   */
  recordingSourceGeneration: integer("recording_source_generation").notNull().default(0),
}, (table) => [
  index("idx_call_logs_tenant_started").on(table.tenantId, table.startedAt),
  index("idx_call_logs_tenant_direction").on(table.tenantId, table.direction),
  index("idx_call_logs_call_uuid").on(table.callUuid),
  index("idx_call_logs_from_number").on(table.fromNumber),
  index("idx_call_logs_to_number").on(table.toNumber),
  index("idx_call_logs_agent_endpoint").on(table.agentEndpointId),
  index("idx_call_logs_tenant_child").on(table.tenantId, table.isChildLeg),
  index("idx_call_logs_tenant_parent_child")
    .on(table.tenantId, table.parentCallId)
    .where(sql`${table.isChildLeg} = true`),
  index("idx_call_logs_contact_id").on(table.contactId),
  index("idx_report_call_logs_contact_latest").on(table.tenantId, table.contactId, table.startedAt.desc(), table.id.desc()),
]);

// isVoicemail is no longer a physical call_logs column (see migration
// voicemails_dedicated_table_columns_removed). It is preserved here as a
// virtual field: an input flag on InsertCallLog that callers can still pass
// to mean "this call is a voicemail" (storage.ts turns it into an explicit
// voicemails-table upsert instead of a column write), and a computed output
// field on CallLog that storage.ts populates from
// EXISTS(SELECT 1 FROM voicemails WHERE call_log_id = call_logs.id) wherever
// a full call-log row is read. Never re-add a physical is_voicemail column.
// It must stay in the runtime Zod schema (not just the TS type) or
// safeParse() silently strips it from any request body before storage ever
// sees it.
export const insertCallLogSchema = createInsertSchema(callLogs).omit({
  id: true,
}).extend({
  isVoicemail: z.boolean().optional(),
});

export type InsertCallLog = z.infer<typeof insertCallLogSchema>;
export type CallLog = typeof callLogs.$inferSelect & { isVoicemail?: boolean };

export const plivoEndpoints = pgTable("plivo_endpoints", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id"),
  username: text("username").notNull(),
  password: text("password").notNull(),
  alias: text("alias"),
  appId: text("app_id"),
  plivoId: text("plivo_id"),
  active: boolean("active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPlivoEndpointSchema = createInsertSchema(plivoEndpoints).omit({
  id: true,
  createdAt: true,
});

export type InsertPlivoEndpoint = z.infer<typeof insertPlivoEndpointSchema>;
export type PlivoEndpoint = typeof plivoEndpoints.$inferSelect;

export const deviceTypeEnum = pgEnum("device_type", ["web", "extension", "mobile"]);

export const userDevices = pgTable("user_devices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  type: deviceTypeEnum("type").notNull(),
  label: varchar("label"),
  plivoEndpointId: varchar("plivo_endpoint_id"),
  ringPriority: integer("ring_priority").notNull().default(1),
  isActive: boolean("is_active").notNull().default(true),
  lastSeenAt: timestamp("last_seen_at"),
  createdAt: timestamp("created_at").defaultNow(),
  revokedAt: timestamp("revoked_at"),
}, (table) => [
  index("idx_user_devices_user_id").on(table.userId),
]);

export const insertUserDeviceSchema = createInsertSchema(userDevices).omit({
  id: true,
  createdAt: true,
});

export type InsertUserDevice = z.infer<typeof insertUserDeviceSchema>;
export type UserDevice = typeof userDevices.$inferSelect;

export const extensionAuthTokens = pgTable("extension_auth_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  deviceId: varchar("device_id").notNull(),
  accessTokenHash: varchar("access_token_hash").notNull(),
  refreshTokenHash: varchar("refresh_token_hash").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  revokedAt: timestamp("revoked_at"),
});

export const insertExtensionAuthTokenSchema = createInsertSchema(extensionAuthTokens).omit({
  id: true,
  createdAt: true,
});

export type InsertExtensionAuthToken = z.infer<typeof insertExtensionAuthTokenSchema>;
export type ExtensionAuthToken = typeof extensionAuthTokens.$inferSelect;

export const customFieldTypeEnum = pgEnum("custom_field_type", ["text", "number", "date", "boolean", "select"]);

export const contactLists = pgTable("contact_lists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  isDefault: boolean("is_default").default(false),
  tenantListNumber: integer("tenant_list_number"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("contact_lists_tenant_name_idx").on(table.tenantId, table.name),
]);

export const insertContactListSchema = createInsertSchema(contactLists).omit({
  id: true,
  createdAt: true,
});

export type InsertContactList = z.infer<typeof insertContactListSchema>;
export type ContactList = typeof contactLists.$inferSelect;

export const contactListMembers = pgTable("contact_list_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  listId: varchar("list_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("contact_list_members_unique_idx").on(table.listId, table.contactId),
  index("idx_contact_list_members_contact_list").on(table.contactId, table.listId),
]);

export const insertContactListMemberSchema = createInsertSchema(contactListMembers).omit({
  id: true,
  createdAt: true,
});

export type InsertContactListMember = z.infer<typeof insertContactListMemberSchema>;
export type ContactListMember = typeof contactListMembers.$inferSelect;

export const contactCustomFields = pgTable("contact_custom_fields", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  fieldKey: text("field_key").notNull(),
  label: text("label").notNull(),
  fieldType: customFieldTypeEnum("field_type").notNull().default("text"),
  isRequired: boolean("is_required").default(false),
  options: text("options").array(),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("contact_custom_fields_tenant_key_idx").on(table.tenantId, table.fieldKey),
]);

export const insertContactCustomFieldSchema = createInsertSchema(contactCustomFields).omit({
  id: true,
  createdAt: true,
});

export type InsertContactCustomField = z.infer<typeof insertContactCustomFieldSchema>;
export type ContactCustomField = typeof contactCustomFields.$inferSelect;

export const contactCustomFieldValues = pgTable("contact_custom_field_values", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  fieldId: varchar("field_id").notNull(),
  value: text("value"),
}, (table) => [
  uniqueIndex("contact_custom_field_values_unique_idx").on(table.contactId, table.fieldId),
]);

export const insertContactCustomFieldValueSchema = createInsertSchema(contactCustomFieldValues).omit({
  id: true,
});

export type InsertContactCustomFieldValue = z.infer<typeof insertContactCustomFieldValueSchema>;
export type ContactCustomFieldValue = typeof contactCustomFieldValues.$inferSelect;

export const afterHoursActionEnum = pgEnum("after_hours_action", ["voicemail", "forward", "message", "hangup"]);
export const dayOfWeekEnum = pgEnum("day_of_week", ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]);

export const businessHours = pgTable("business_hours", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  dayOfWeek: dayOfWeekEnum("day_of_week").notNull(),
  isOpen: boolean("is_open").notNull().default(true),
  openTime: text("open_time").notNull().default("09:00"),
  closeTime: text("close_time").notNull().default("17:00"),
  openTime2: text("open_time_2"),
  closeTime2: text("close_time_2"),
}, (table) => [
  uniqueIndex("business_hours_tenant_day_idx").on(table.tenantId, table.dayOfWeek),
]);

export const insertBusinessHoursSchema = createInsertSchema(businessHours).omit({
  id: true,
});

export type InsertBusinessHours = z.infer<typeof insertBusinessHoursSchema>;
export type BusinessHours = typeof businessHours.$inferSelect;

export const holidayCalendar = pgTable("holiday_calendar", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  date: text("date").notNull(),
  country: text("country").notNull().default("US"),
  recurring: boolean("recurring").notNull().default(true),
});

export type HolidayCalendar = typeof holidayCalendar.$inferSelect;

export const tenantHolidays = pgTable("tenant_holidays", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  holidayId: varchar("holiday_id").notNull(),
  enabled: boolean("enabled").notNull().default(true),
}, (table) => [
  uniqueIndex("tenant_holidays_unique_idx").on(table.tenantId, table.holidayId),
]);

export const insertTenantHolidaySchema = createInsertSchema(tenantHolidays).omit({
  id: true,
});

export type InsertTenantHoliday = z.infer<typeof insertTenantHolidaySchema>;
export type TenantHoliday = typeof tenantHolidays.$inferSelect;

export const tenantBusinessSettings = pgTable("tenant_business_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().unique(),
  defaultSmsPhoneNumberId: varchar("default_sms_phone_number_id"),
  timezone: text("timezone").notNull().default("America/New_York"),
  afterHoursAction: afterHoursActionEnum("after_hours_action").notNull().default("message"),
  afterHoursForwardNumber: text("after_hours_forward_number"),
  afterHoursMessage: text("after_hours_message").default("We are currently closed. Please call back during our business hours."),
  voicemailGreetingType: text("voicemail_greeting_type").$type<"tts" | "audio">().default("tts"),
  voicemailGreeting: text("voicemail_greeting").default("Please leave a message after the tone."),
  voicemailGreetingAudioUrl: text("voicemail_greeting_audio_url"),
  voicemailEmailRecipients: text("voicemail_email_recipients").array(),
  afterHoursEnabled: boolean("after_hours_enabled").notNull().default(false),
  dispositionEnabled: boolean("disposition_enabled").notNull().default(false),
  forceDisposition: boolean("force_disposition").notNull().default(false),
  acwInboundEnabled: boolean("acw_inbound_enabled").notNull().default(false),
  acwInboundStatus: text("acw_inbound_status").default("after_call_work"),
  acwInboundDuration: integer("acw_inbound_duration").default(30),
  acwOutboundEnabled: boolean("acw_outbound_enabled").notNull().default(false),
  acwOutboundStatus: text("acw_outbound_status").default("after_call_work"),
  acwOutboundDuration: integer("acw_outbound_duration").default(30),
  ttsVoice: text("tts_voice").default("Polly.Joanna"),
  screenPopEnableContactProfile: boolean("screen_pop_enable_contact_profile").default(true),
  screenPopEnableNotes: boolean("screen_pop_enable_notes").default(true),
  screenPopEnableScript: boolean("screen_pop_enable_script").default(false),
  screenPopEnableFloatingScript: boolean("screen_pop_enable_floating_script").default(false),
  screenPopEnableExternalLinks: boolean("screen_pop_enable_external_links").default(false),
  screenPopAutoOpenOutbound: boolean("screen_pop_auto_open_outbound").default(true),
  screenPopTabFocus: text("screen_pop_tab_focus").default("profile"),
  statusAfterLogin: text("status_after_login").default("ready"),
  statusAfterLogout: text("status_after_logout").default("offline"),
  outboundCallerIdDisplay: text("outbound_caller_id_display").default("caller_name"),
  anthropicApiKey: text("anthropic_api_key"),
  plivoByocAuthId: text("plivo_byoc_auth_id"),
  plivoByocAuthToken: text("plivo_byoc_auth_token"),
  notReadyAllowQueueCalls: boolean("not_ready_allow_queue_calls").notNull().default(false),
  notReadyAllowIncomingCalls: boolean("not_ready_allow_incoming_calls").notNull().default(false),
  notReadyAllowTransferCalls: boolean("not_ready_allow_transfer_calls").notNull().default(false),
  notReadyAllowInternalCalls: boolean("not_ready_allow_internal_calls").notNull().default(false),
  acwAllowQueueCalls: boolean("acw_allow_queue_calls").notNull().default(false),
  acwAllowIncomingCalls: boolean("acw_allow_incoming_calls").notNull().default(false),
  acwAllowTransferCalls: boolean("acw_allow_transfer_calls").notNull().default(false),
  acwAllowInternalCalls: boolean("acw_allow_internal_calls").notNull().default(false),
  aiBotEnabled: boolean("ai_bot_enabled").notNull().default(false),
  aiBotName: text("ai_bot_name").default("AI Assistant"),
  aiBotSystemPrompt: text("ai_bot_system_prompt"),
  aiBotMaxTokensPerHour: integer("ai_bot_max_tokens_per_hour").default(50000),
  aiBotModel: text("ai_bot_model"),
  aiBotAutoAssignSms: boolean("ai_bot_auto_assign_sms").notNull().default(false),
  aiBotAutoAssignLiveChat: boolean("ai_bot_auto_assign_live_chat").notNull().default(false),
  aiBotAutoAssignWhatsapp: boolean("ai_bot_auto_assign_whatsapp").notNull().default(false),
  aiBotAutoAssignFacebook: boolean("ai_bot_auto_assign_facebook").notNull().default(false),
  aiBotAutoAssignInstagram: boolean("ai_bot_auto_assign_instagram").notNull().default(false),
  aiBotLiveChatMode: text("ai_bot_live_chat_mode").default("off"),
  aiBotAfterHoursEnabled: boolean("ai_bot_after_hours_enabled").notNull().default(false),
  aiBotDailySummaryEnabled: boolean("ai_bot_daily_summary_enabled").notNull().default(false),
  aiBotDailySummaryTime: text("ai_bot_daily_summary_time").default("08:00"),
  aiBotDailySummaryRecipients: text("ai_bot_daily_summary_recipients"),
  zendeskEnabled: boolean("zendesk_enabled").notNull().default(false),
  zendeskSubdomain: text("zendesk_subdomain"),
  zendeskEmail: text("zendesk_email"),
  zendeskApiToken: text("zendesk_api_token"),
  zendeskAuthMode: text("zendesk_auth_mode").default("token"),
  zendeskOAuthAccessToken: text("zendesk_oauth_access_token"),
  zendeskOAuthRefreshToken: text("zendesk_oauth_refresh_token"),
  zendeskOAuthTokenExpiry: timestamp("zendesk_oauth_token_expiry"),
  zendeskConnectedEmail: text("zendesk_connected_email"),
  zendeskOAuthClientId: text("zendesk_oauth_client_id"),
  zendeskOAuthClientSecret: text("zendesk_oauth_client_secret"),
  zendeskTicketBodyMode: text("zendesk_ticket_body_mode").$type<"summary" | "transcript">().default("summary"),
  zendeskAutoCreateAfterHours: boolean("zendesk_auto_create_after_hours").notNull().default(false),
  zendeskDefaultTags: text("zendesk_default_tags"),
  zendeskUseCustomerRequester: boolean("zendesk_use_customer_requester").notNull().default(false),
  zendeskDoNotSetRequester: boolean("zendesk_do_not_set_requester").notNull().default(false),
  zendeskRequesterEmail: text("zendesk_requester_email"),
  zendeskInternalNote: boolean("zendesk_internal_note").notNull().default(false),
  zendeskHideExtraTags: boolean("zendesk_hide_extra_tags").notNull().default(false),
  clickToCallExtensionEnabled: boolean("click_to_call_extension_enabled").notNull().default(false),
  inactivityTimeoutMinutes: integer("inactivity_timeout_minutes").notNull().default(60),
  csatEnabled: boolean("csat_enabled").notNull().default(false),
  csatAutoTrigger: boolean("csat_auto_trigger").notNull().default(false),
  csatCooldownDays: integer("csat_cooldown_days").notNull().default(7),
  csatSamplingRate: integer("csat_sampling_rate").notNull().default(100),
  csatLowScoreThreshold: integer("csat_low_score_threshold").notNull().default(2),
  csatAlertEmails: text("csat_alert_emails"),
  csatSkipShortCalls: boolean("csat_skip_short_calls").notNull().default(true),
  csatSkipVoicemail: boolean("csat_skip_voicemail").notNull().default(true),
  csatSkipNoAnswer: boolean("csat_skip_no_answer").notNull().default(true),
  csatSkipTransferred: boolean("csat_skip_transferred").notNull().default(true),
  csatSameCallEnabled: boolean("csat_same_call_enabled").notNull().default(false),
  csatOutboundEnabled: boolean("csat_outbound_enabled").notNull().default(false),
  csatManualEnabled: boolean("csat_manual_enabled").notNull().default(true),
  csatQ1Text: text("csat_q1_text").default("Thank you for calling. We'd love your feedback. On a scale of 1 to 5, how satisfied were you with the support you received today? Press 1 for Very Satisfied, Press 2 for Satisfied, Press 3 for Neutral, Press 4 for Dissatisfied, Press 5 for Very Dissatisfied. Press 9 to skip."),
  csatQ1RecordingId: varchar("csat_q1_recording_id"),
  csatQ1UseRecording: boolean("csat_q1_use_recording").notNull().default(false),
  csatQ2Text: text("csat_q2_text").default("Was your issue fully resolved today? Press 1 for Yes, Press 2 for No, Press 9 to skip."),
  csatQ2RecordingId: varchar("csat_q2_recording_id"),
  csatQ2UseRecording: boolean("csat_q2_use_recording").notNull().default(false),
  csatThankYouText: text("csat_thank_you_text").default("Thank you for your feedback. We appreciate your time. Goodbye."),
  csatThankYouRecordingId: varchar("csat_thank_you_recording_id"),
  csatThankYouUseRecording: boolean("csat_thank_you_use_recording").notNull().default(false),
  csatYesDigit: varchar("csat_yes_digit", { length: 1 }).notNull().default("1"),
  csatNoDigit: varchar("csat_no_digit", { length: 1 }).notNull().default("2"),
  csatSkipDigit: varchar("csat_skip_digit", { length: 1 }).notNull().default("9"),
  warmTransferKeepAliveAfterCustomerHangup: boolean("warm_transfer_keep_alive_after_customer_hangup").notNull().default(false),
  recordingDownloadEnabled: boolean("recording_download_enabled").notNull().default(false),
  deepgramApiKey: text("deepgram_api_key"),
  conversationsTabStatusEnabled: boolean("conversations_tab_status_enabled").notNull().default(false),
  conversationsTabStatus: text("conversations_tab_status").default("not_ready"),
  conversationDispositionEnabled: boolean("conversation_disposition_enabled").notNull().default(false),
  conversationDispositionRequired: boolean("conversation_disposition_required").notNull().default(false),
  appointmentWebhookEnabled: boolean("appointment_webhook_enabled").notNull().default(false),
  appointmentWebhookSecret: text("appointment_webhook_secret"),
  appointmentBookedDispositionIds: text("appointment_booked_disposition_ids").array(),
  contactTableCustomFieldIds: text("contact_table_custom_field_ids").array().default(sql`ARRAY[]::text[]`),
  slaThresholdSeconds: integer("sla_threshold_seconds").default(20),
  slaTargetPercent: integer("sla_target_percent").default(100),
  /** Per-tenant feature flag for SIP health routing. Global kill-switch: SIP_HEALTH_ROUTING_ENABLED env var. Both default false/disabled. */
  sipHealthRoutingEnabled: boolean("sip_health_routing_enabled").notNull().default(false),
  /** Enable Anthropic prompt caching for the AI Bot. Defaults to true (cache enabled with 5-min TTL). */
  aiBotCacheEnabled: boolean("ai_bot_cache_enabled").notNull().default(true),
  /** Prompt cache TTL in seconds. 300 = 5 minutes (default), 3600 = 1 hour (for sustained traffic). */
  aiBotCacheTtl: integer("ai_bot_cache_ttl").notNull().default(300),
});

export const insertTenantBusinessSettingsSchema = createInsertSchema(tenantBusinessSettings).omit({
  id: true,
});

export type InsertTenantBusinessSettings = z.infer<typeof insertTenantBusinessSettingsSchema>;
export type TenantBusinessSettings = typeof tenantBusinessSettings.$inferSelect;

export const dispositions = pgTable("dispositions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  parentId: varchar("parent_id"),
  name: text("name").notNull(),
  legacyId: text("legacy_id"),
  isActive: boolean("is_active").default(true),
  sortOrder: integer("sort_order").default(0),
  callDirection: text("call_direction").default("both"),
  forceEmail: boolean("force_email").default(false),
  context: text("context").default("call"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDispositionSchema = createInsertSchema(dispositions).omit({
  id: true,
  createdAt: true,
});

export type InsertDisposition = z.infer<typeof insertDispositionSchema>;
export type Disposition = typeof dispositions.$inferSelect;

export const recordingCategoryEnum = pgEnum("recording_category", ["ivr_greeting", "hold_music", "queue_announcement", "voicemail_greeting", "after_hours", "voicemail_drop", "other", "sound_notification_conversation", "sound_notification_queue", "sound_notification"]);

export const recordings = pgTable("recordings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  category: recordingCategoryEnum("category").notNull().default("other"),
  fileName: text("file_name").notNull(),
  fileUrl: text("file_url").notNull(),
  fileSize: integer("file_size"),
  duration: integer("duration"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertRecordingSchema = createInsertSchema(recordings).omit({
  id: true,
  createdAt: true,
});

export type InsertRecording = z.infer<typeof insertRecordingSchema>;
export type Recording = typeof recordings.$inferSelect;

export const extensionTargetTypeEnum = pgEnum("extension_target_type", ["agent", "external"]);

export const extensions = pgTable("extensions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  extensionNumber: text("extension_number").notNull(),
  label: text("label"),
  targetType: extensionTargetTypeEnum("target_type").notNull(),
  targetEndpointId: varchar("target_endpoint_id"),
  targetPhoneNumber: text("target_phone_number"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertExtensionSchema = createInsertSchema(extensions).omit({
  id: true,
  createdAt: true,
});

export type InsertExtension = z.infer<typeof insertExtensionSchema>;
export type Extension = typeof extensions.$inferSelect;

export const customAgentStatuses = pgTable("custom_agent_statuses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  color: text("color").notNull().default("bg-gray-500"),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").default(0),
  allowQueueCalls: boolean("allow_queue_calls").notNull().default(false),
  allowIncomingCalls: boolean("allow_incoming_calls").notNull().default(false),
  allowTransferCalls: boolean("allow_transfer_calls").notNull().default(false),
  allowInternalCalls: boolean("allow_internal_calls").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCustomAgentStatusSchema = createInsertSchema(customAgentStatuses).omit({
  id: true,
  createdAt: true,
});

export type InsertCustomAgentStatus = z.infer<typeof insertCustomAgentStatusSchema>;
export type CustomAgentStatus = typeof customAgentStatuses.$inferSelect;

export const conversationChannelEnum = pgEnum("conversation_channel", ["sms", "email", "livechat", "whatsapp", "facebook", "instagram", "voice"]);
export const conversationStatusEnum = pgEnum("conversation_status", ["open", "assigned", "resolved", "closed"]);
export const messageDirectionEnum = pgEnum("message_direction", ["inbound", "outbound"]);

export const conversations = pgTable("conversations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  tenantConversationNumber: integer("tenant_conversation_number"),
  channel: conversationChannelEnum("channel").notNull(),
  status: conversationStatusEnum("conversation_status").notNull().default("open"),
  contactId: varchar("contact_id"),
  assigneeUserId: varchar("assignee_user_id"),
  subject: text("subject"),
  sourceAddress: text("source_address").notNull(),
  inboxAddress: text("inbox_address").notNull(),
  sourceAddressNorm: text("source_address_norm"),
  inboxAddressNorm: text("inbox_address_norm"),
  lastMessageAt: timestamp("last_message_at").defaultNow(),
  lastMessagePreview: text("last_message_preview"),
  unreadCount: integer("unread_count").default(0),
  unassignedAt: timestamp("unassigned_at"),
  metadata: jsonb("metadata"),
  aiSummary: text("ai_summary"),
  aiSummaryCreatedAt: timestamp("ai_summary_created_at"),
  zendeskTicketId: text("zendesk_ticket_id"),
  zendeskTicketUrl: text("zendesk_ticket_url"),
  disposition: text("disposition"),
  dispositionNote: text("disposition_note"),
  closedAt: timestamp("closed_at"),
  activityImportLogId: varchar("activity_import_log_id"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_conversations_tenant_status").on(table.tenantId, table.status),
  index("idx_conversations_tenant_last_msg").on(table.tenantId, table.lastMessageAt),
  index("idx_conversations_tenant_closed_at").on(table.tenantId, table.closedAt),
  index("idx_conversations_tenant_assignee_status_last_msg")
    .on(table.tenantId, table.assigneeUserId, table.status, table.lastMessageAt.desc())
    .where(sql`${table.assigneeUserId} IS NOT NULL`),
]);

export const insertConversationSchema = createInsertSchema(conversations).omit({
  id: true,
  createdAt: true,
});

export type InsertConversation = z.infer<typeof insertConversationSchema>;
export type Conversation = typeof conversations.$inferSelect;

export const conversationMessages = pgTable("conversation_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  conversationId: varchar("conversation_id").notNull(),
  direction: messageDirectionEnum("message_direction").notNull(),
  channel: conversationChannelEnum("message_channel").notNull(),
  body: text("body").notNull(),
  fromAddress: text("from_address").notNull(),
  toAddress: text("to_address").notNull(),
  externalMessageId: text("external_message_id"),
  senderUserId: varchar("sender_user_id"),
  deliveryStatus: text("delivery_status").default("sent"),
  deliveryError: text("delivery_error"),
  isInternal: boolean("is_internal").default(false),
  attachments: jsonb("attachments").default([]),
  isRead: boolean("is_read").default(true),
  sentAt: timestamp("sent_at").defaultNow(),
}, (table) => [
  index("idx_conv_messages_conversation").on(table.conversationId, table.sentAt),
  index("idx_conv_messages_tenant").on(table.tenantId),
]);

export const insertConversationMessageSchema = createInsertSchema(conversationMessages).omit({
  id: true,
  sentAt: true,
});

export type InsertConversationMessage = z.infer<typeof insertConversationMessageSchema>;
export type ConversationMessage = typeof conversationMessages.$inferSelect;

export const smsDispatches = pgTable("sms_dispatches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  direction: text("direction").notNull().$type<"inbound" | "outbound">(),
  audience: text("audience").notNull().$type<"customer" | "internal_alert">(),
  sourceType: text("source_type").notNull(),
  sourceId: varchar("source_id"),
  idempotencyKey: text("idempotency_key").notNull(),
  actorType: text("actor_type").notNull().$type<"user" | "bot" | "system" | "api" | "customer">(),
  actorUserId: varchar("actor_user_id"),
  provider: text("provider").notNull().$type<"plivo" | "twilio" | "didww">(),
  phoneNumberId: varchar("phone_number_id"),
  fromAddress: text("from_address").notNull(),
  toAddress: text("to_address").notNull(),
  contactId: varchar("contact_id"),
  conversationId: varchar("conversation_id"),
  conversationMessageId: varchar("conversation_message_id"),
  externalMessageId: text("external_message_id"),
  status: text("status").notNull().default("pending").$type<"pending" | "queued" | "sent" | "delivered" | "failed">(),
  providerStatus: text("provider_status"),
  errorCode: text("error_code"),
  errorMessage: text("error_message"),
  bodyLength: integer("body_length").notNull().default(0),
  mediaCount: integer("media_count").notNull().default(0),
  billableAmount: numeric("billable_amount", { precision: 12, scale: 4 }),
  billingStatus: text("billing_status").notNull().default("not_applicable").$type<"not_applicable" | "pending" | "billed" | "failed">(),
  billingLedgerId: varchar("billing_ledger_id"),
  acceptedAt: timestamp("accepted_at", { withTimezone: true }),
  deliveredAt: timestamp("delivered_at", { withTimezone: true }),
  failedAt: timestamp("failed_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  uniqueIndex("sms_dispatches_tenant_idempotency_unique").on(table.tenantId, table.idempotencyKey),
  uniqueIndex("sms_dispatches_provider_external_unique").on(table.provider, table.externalMessageId),
  index("idx_sms_dispatches_tenant_created").on(table.tenantId, table.createdAt),
  index("idx_sms_dispatches_contact_created").on(table.contactId, table.createdAt),
  index("idx_sms_dispatches_conversation").on(table.conversationId),
  index("idx_sms_dispatches_source").on(table.sourceType, table.sourceId),
  index("idx_sms_dispatches_status").on(table.status, table.updatedAt),
]);

export const insertSmsDispatchSchema = createInsertSchema(smsDispatches).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertSmsDispatch = z.infer<typeof insertSmsDispatchSchema>;
export type SmsDispatch = typeof smsDispatches.$inferSelect;

export const smsWebhookQuarantine = pgTable("sms_webhook_quarantine", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  provider: text("provider").notNull().$type<"plivo" | "twilio" | "didww">(),
  externalMessageId: text("external_message_id"),
  fromAddress: text("from_address"),
  toAddress: text("to_address"),
  reason: text("reason").notNull(),
  bodyLength: integer("body_length").notNull().default(0),
  mediaCount: integer("media_count").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  uniqueIndex("sms_webhook_quarantine_provider_external_unique").on(table.provider, table.externalMessageId),
  index("idx_sms_webhook_quarantine_created").on(table.createdAt),
]);

export const insertSmsWebhookQuarantineSchema = createInsertSchema(smsWebhookQuarantine).omit({
  id: true,
  createdAt: true,
});
export type InsertSmsWebhookQuarantine = z.infer<typeof insertSmsWebhookQuarantineSchema>;
export type SmsWebhookQuarantine = typeof smsWebhookQuarantine.$inferSelect;

export const conversationAssignments = pgTable("conversation_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  conversationId: varchar("conversation_id").notNull(),
  userId: varchar("user_id").notNull(),
  assignedBy: varchar("assigned_by"),
  active: boolean("active").default(true),
  assignedAt: timestamp("assigned_at").defaultNow(),
}, (table) => [
  index("idx_conversation_assignments_latest").on(table.conversationId, table.assignedAt.desc()),
]);

export const insertConversationAssignmentSchema = createInsertSchema(conversationAssignments).omit({
  id: true,
  assignedAt: true,
});

export type InsertConversationAssignment = z.infer<typeof insertConversationAssignmentSchema>;
export type ConversationAssignment = typeof conversationAssignments.$inferSelect;

export const smsRoutingAssignments = pgTable("sms_routing_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  phoneNumberId: varchar("phone_number_id").notNull(),
  userId: varchar("user_id").notNull(),
  stickyRouting: boolean("sticky_routing").default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSmsRoutingAssignmentSchema = createInsertSchema(smsRoutingAssignments).omit({
  id: true,
  createdAt: true,
});

export type InsertSmsRoutingAssignment = z.infer<typeof insertSmsRoutingAssignmentSchema>;
export type SmsRoutingAssignment = typeof smsRoutingAssignments.$inferSelect;

export const outboundCallerIdAssignments = pgTable("outbound_caller_id_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  phoneNumberId: varchar("phone_number_id").notNull(),
  userId: varchar("user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertOutboundCallerIdAssignmentSchema = createInsertSchema(outboundCallerIdAssignments).omit({
  id: true,
  createdAt: true,
});

export type InsertOutboundCallerIdAssignment = z.infer<typeof insertOutboundCallerIdAssignmentSchema>;
export type OutboundCallerIdAssignment = typeof outboundCallerIdAssignments.$inferSelect;

export const smsRoutingPolicies = pgTable("sms_routing_policies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  phoneNumberId: varchar("phone_number_id").notNull(),
  strategy: varchar("strategy", { length: 50 }).notNull().default("inbox_only"),
  lastAssignedUserId: varchar("last_assigned_user_id"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertSmsRoutingPolicySchema = createInsertSchema(smsRoutingPolicies).omit({
  id: true,
  updatedAt: true,
});

export type InsertSmsRoutingPolicy = z.infer<typeof insertSmsRoutingPolicySchema>;
export type SmsRoutingPolicy = typeof smsRoutingPolicies.$inferSelect;

export const userNotificationState = pgTable("user_notification_state", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  lastSeenAt: timestamp("last_seen_at").defaultNow(),
  unassignedLastSeenAt: timestamp("unassigned_last_seen_at"),
  vmLastSeenAt: timestamp("vm_last_seen_at"),
  missedCallLastSeenAt: timestamp("missed_call_last_seen_at"),
});

export const insertUserNotificationStateSchema = createInsertSchema(userNotificationState).omit({
  id: true,
});

export type InsertUserNotificationState = z.infer<typeof insertUserNotificationStateSchema>;
export type UserNotificationState = typeof userNotificationState.$inferSelect;

export const billingModeEnum = pgEnum("billing_mode", ["prepaid", "postpaid"]);
export const ledgerTypeEnum = pgEnum("ledger_type", ["credit", "debit"]);
export const ledgerCategoryEnum = pgEnum("ledger_category", [
  "admin_credit", "voice_call", "sms", "phone_number", "refund", "adjustment", "stripe_payment", "voice_ai"
]);

export const tenantBillingSettings = pgTable("tenant_billing_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().unique(),
  billingMode: billingModeEnum("billing_mode").notNull().default("postpaid"),
  balance: numeric("balance", { precision: 12, scale: 4 }).default("0"),
  lowBalanceThreshold: numeric("low_balance_threshold", { precision: 12, scale: 4 }).default("5"),
  subscriptionPricePerUser: numeric("subscription_price_per_user", { precision: 12, scale: 4 }).default("50"),
  currency: text("currency").default("USD"),
  stripeCustomerId: text("stripe_customer_id"),
  stripePaymentMethodId: text("stripe_payment_method_id"),
  stripePaymentMethodLast4: text("stripe_payment_method_last4"),
  stripePaymentMethodBrand: text("stripe_payment_method_brand"),
  autoRechargeEnabled: boolean("auto_recharge_enabled").default(false),
  autoRechargeAmount: numeric("auto_recharge_amount", { precision: 12, scale: 4 }).default("50"),
  autoRechargeThreshold: numeric("auto_recharge_threshold", { precision: 12, scale: 4 }).default("10"),
  billingCycleDay: integer("billing_cycle_day").default(1),
  billingCycleLength: text("billing_cycle_length").default("monthly"),
  billingCycleAnchor: timestamp("billing_cycle_anchor"),
  discountType: text("discount_type"),
  discountValue: numeric("discount_value", { precision: 12, scale: 4 }),
  lastPostpaidChargeAt: timestamp("last_postpaid_charge_at"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  stripeAgentPriceId: text("stripe_agent_price_id"),
  stripePhoneNumberPriceId: text("stripe_phone_number_price_id"),
  taxEnabled: boolean("tax_enabled").default(false),
  taxPercentage: numeric("tax_percentage", { precision: 6, scale: 3 }).default("0"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTenantBillingSettingsSchema = createInsertSchema(tenantBillingSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertTenantBillingSettings = z.infer<typeof insertTenantBillingSettingsSchema>;
export type TenantBillingSettings = typeof tenantBillingSettings.$inferSelect;

export const subscriptionPendingRemovals = pgTable("subscription_pending_removals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  itemType: text("item_type").notNull(),
  quantity: integer("quantity").notNull().default(1),
  removedAt: timestamp("removed_at").defaultNow(),
  processedAt: timestamp("processed_at"),
});
export type SubscriptionPendingRemoval = typeof subscriptionPendingRemovals.$inferSelect;

export const tenantPricing = pgTable("tenant_pricing", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  itemType: text("item_type").notNull(),
  label: text("label").notNull(),
  unitPrice: numeric("unit_price", { precision: 12, scale: 6 }).notNull(),
  unit: text("unit").notNull().default("per_minute"),
  country: text("country"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTenantPricingSchema = createInsertSchema(tenantPricing).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertTenantPricing = z.infer<typeof insertTenantPricingSchema>;
export type TenantPricing = typeof tenantPricing.$inferSelect;

export const billingLedger = pgTable("billing_ledger", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  type: ledgerTypeEnum("type").notNull(),
  category: ledgerCategoryEnum("category").notNull(),
  amount: numeric("amount", { precision: 12, scale: 4 }).notNull(),
  balanceAfter: numeric("balance_after", { precision: 12, scale: 4 }),
  description: text("description"),
  referenceId: varchar("reference_id"),
  referenceType: text("reference_type"),
  stripePaymentId: text("stripe_payment_id"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_billing_ledger_tenant_ref").on(table.tenantId, table.referenceId),
  index("idx_billing_ledger_tenant_created").on(table.tenantId, table.createdAt),
]);

export const insertBillingLedgerSchema = createInsertSchema(billingLedger).omit({
  id: true,
  createdAt: true,
});
export type InsertBillingLedger = z.infer<typeof insertBillingLedgerSchema>;
export type BillingLedger = typeof billingLedger.$inferSelect;

export const billingInvoices = pgTable("billing_invoices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  totalVoice: numeric("total_voice", { precision: 12, scale: 4 }).default("0"),
  totalSms: numeric("total_sms", { precision: 12, scale: 4 }).default("0"),
  totalPhoneNumbers: numeric("total_phone_numbers", { precision: 12, scale: 4 }).default("0"),
  totalOther: numeric("total_other", { precision: 12, scale: 4 }).default("0"),
  totalAgents: numeric("total_agents", { precision: 12, scale: 4 }).default("0"),
  discountAmount: numeric("discount_amount", { precision: 12, scale: 4 }).default("0"),
  taxAmount: numeric("tax_amount", { precision: 12, scale: 4 }).default("0"),
  taxPercentage: numeric("tax_percentage", { precision: 6, scale: 3 }).default("0"),
  grandTotal: numeric("grand_total", { precision: 12, scale: 4 }).default("0"),
  voiceMinutes: integer("voice_minutes").default(0),
  smsCount: integer("sms_count").default(0),
  callCount: integer("call_count").default(0),
  agentCount: integer("agent_count").default(0),
  status: text("status").default("draft"),
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  stripeChargeStatus: text("stripe_charge_status"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertBillingInvoiceSchema = createInsertSchema(billingInvoices).omit({
  id: true,
  createdAt: true,
});
export type InsertBillingInvoice = z.infer<typeof insertBillingInvoiceSchema>;
export type BillingInvoice = typeof billingInvoices.$inferSelect;

export const userSettings = pgTable("user_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  userId: varchar("user_id").notNull().unique(),
  defaultCallerId: varchar("default_caller_id"),
  smsAssignedNumberId: varchar("sms_assigned_number_id"),
  voicemailGreetingUrl: text("voicemail_greeting_url"),
  timezone: text("timezone").default("America/New_York"),
  timezoneIsCustom: boolean("timezone_is_custom").default(false),
  emailSenderAddress: text("email_sender_address"),
  postmarkSignatureId: integer("postmark_signature_id"),
  emailSignatureConfirmed: boolean("email_signature_confirmed").default(false),
  voicemailDropEnabled: boolean("voicemail_drop_enabled").default(false),
  voicemailDropRecordingId: varchar("voicemail_drop_recording_id"),
  deviceRingStrategy: text("device_ring_strategy").$type<"priority" | "ring_all_devices">().default("priority"),
  autoAnswerEnabled: boolean("auto_answer_enabled").default(false),
});

export const insertUserSettingsSchema = createInsertSchema(userSettings).omit({ id: true });
export type InsertUserSettings = z.infer<typeof insertUserSettingsSchema>;
export type UserSettings = typeof userSettings.$inferSelect;

export const dayOffRequestStatusEnum = pgEnum("day_off_request_status", ["pending", "approved", "denied"]);

export const agentSchedules = pgTable("agent_schedules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  userId: varchar("user_id").notNull(),
  dayOfWeek: integer("day_of_week").notNull(),
  startTime: text("start_time").notNull(),
  endTime: text("end_time").notNull(),
  isOff: boolean("is_off").default(false),
  weekStartDate: text("week_start_date"), // null = default template; YYYY-MM-DD = week-specific override
  isDayOffApproved: boolean("is_day_off_approved").default(false), // true = written by an approved day-off request
});

export const insertAgentScheduleSchema = createInsertSchema(agentSchedules).omit({ id: true });
export type InsertAgentSchedule = z.infer<typeof insertAgentScheduleSchema>;
export type AgentSchedule = typeof agentSchedules.$inferSelect;

export const dayOffRequests = pgTable("day_off_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  userId: varchar("user_id").notNull(),
  startDate: text("start_date").notNull(),
  endDate: text("end_date").notNull(),
  reason: text("reason"),
  status: dayOffRequestStatusEnum("status").notNull().default("pending"),
  reviewedByUserId: varchar("reviewed_by_user_id"),
  reviewedAt: timestamp("reviewed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDayOffRequestSchema = createInsertSchema(dayOffRequests).omit({ id: true, reviewedAt: true, createdAt: true });
export type InsertDayOffRequest = z.infer<typeof insertDayOffRequestSchema>;
export type DayOffRequest = typeof dayOffRequests.$inferSelect;

export const ticketStatusEnum = pgEnum("ticket_status", ["open", "pending", "resolved", "closed"]);
export const ticketPriorityEnum = pgEnum("ticket_priority", ["low", "medium", "high", "urgent"]);

export const ticketInboxes = pgTable("ticket_inboxes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  postmarkInboundAddress: text("postmark_inbound_address"),
  postmarkSignatureId: integer("postmark_signature_id"),
  signatureConfirmed: boolean("signature_confirmed").default(false),
  postmarkServerToken: text("postmark_server_token"),
  isActive: boolean("is_active").default(true),
  ticketNumberingEnabled: boolean("ticket_numbering_enabled").default(true),
  senderDisplayNameMode: text("sender_display_name_mode").notNull().default("agent"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertTicketInboxSchema = createInsertSchema(ticketInboxes).omit({
  id: true,
  postmarkSignatureId: true,
  signatureConfirmed: true,
  createdAt: true,
});
export type InsertTicketInbox = z.infer<typeof insertTicketInboxSchema>;
export type TicketInbox = typeof ticketInboxes.$inferSelect;

export const ticketInboxAgents = pgTable("ticket_inbox_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  inboxId: varchar("inbox_id").notNull(),
  userId: varchar("user_id").notNull(),
});

export const insertTicketInboxAgentSchema = createInsertSchema(ticketInboxAgents).omit({ id: true });
export type InsertTicketInboxAgent = z.infer<typeof insertTicketInboxAgentSchema>;
export type TicketInboxAgent = typeof ticketInboxAgents.$inferSelect;

export const tickets = pgTable("tickets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ticketNumber: integer("ticket_number"),
  tenantId: varchar("tenant_id").notNull(),
  inboxId: varchar("inbox_id").notNull(),
  contactId: varchar("contact_id"),
  assigneeUserId: varchar("assignee_user_id"),
  status: ticketStatusEnum("status").notNull().default("open"),
  priority: ticketPriorityEnum("priority").notNull().default("medium"),
  subject: text("subject"),
  fromEmail: text("from_email").notNull(),
  fromName: text("from_name"),
  lastMessageAt: timestamp("last_message_at").defaultNow(),
  unreadCount: integer("unread_count").default(1),
  postmarkMessageId: text("postmark_message_id"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_tickets_tenant_status").on(table.tenantId, table.status),
  index("idx_tickets_tenant_created").on(table.tenantId, table.createdAt),
  index("idx_tickets_inbox").on(table.inboxId),
  index("idx_tickets_tenant_assignee_status_created")
    .on(table.tenantId, table.assigneeUserId, table.status, table.createdAt.desc())
    .where(sql`${table.assigneeUserId} IS NOT NULL`),
  index("idx_tickets_tenant_priority_created")
    .on(table.tenantId, table.priority, table.createdAt.desc()),
]);

export const insertTicketSchema = createInsertSchema(tickets).omit({
  id: true,
  ticketNumber: true,
  lastMessageAt: true,
  unreadCount: true,
  createdAt: true,
});
export type InsertTicket = z.infer<typeof insertTicketSchema>;
export type Ticket = typeof tickets.$inferSelect;

export const ticketMessages = pgTable("ticket_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ticketId: varchar("ticket_id").notNull(),
  direction: text("direction").notNull(),
  body: text("body").notNull(),
  htmlBody: text("html_body"),
  fromAddress: text("from_address").notNull(),
  toAddress: text("to_address").notNull(),
  sentAt: timestamp("sent_at").defaultNow(),
  isInternal: boolean("is_internal").default(false),
  senderUserId: varchar("sender_user_id"),
  postmarkMessageId: text("postmark_message_id"),
  deliveryStatus: text("delivery_status").default("sent"),
  attachments: jsonb("attachments").default([]),
}, (table) => [
  index("idx_ticket_messages_ticket").on(table.ticketId, table.sentAt),
]);

export const insertTicketMessageSchema = createInsertSchema(ticketMessages).omit({
  id: true,
  sentAt: true,
});
export type InsertTicketMessage = z.infer<typeof insertTicketMessageSchema>;
export type TicketMessage = typeof ticketMessages.$inferSelect;

export const liveChatWidgetStatusEnum = pgEnum("live_chat_widget_status", ["active", "inactive"]);

export const liveChatWidgets = pgTable("live_chat_widgets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  publicKey: varchar("public_key").notNull().unique(),
  status: liveChatWidgetStatusEnum("status").notNull().default("active"),
  availabilityVersion: integer("availability_version").notNull().default(0),
  availabilityUpdatedAt: timestamp("availability_updated_at").notNull().defaultNow(),
  allowedDomains: text("allowed_domains").array().default([]),
  theme: jsonb("theme").default({
    primaryColor: "#2563eb",
    headerText: "Chat with us",
    welcomeMessage: "Hi there! How can we help you today?",
    offlineMessage: "We are currently offline. Please leave a message and we will get back to you.",
    position: "bottom-right",
    bubbleIcon: "chat",
    headerTextColor: "#ffffff",
    fontFamily: "inherit",
  }),
  preChatForm: jsonb("pre_chat_form").default({
    enabled: true,
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "email", label: "Email", type: "email", required: true },
    ],
  }),
  autoReplyConfig: jsonb("auto_reply_config").default({
    enabled: false,
    firstMessageReply: "Thanks for reaching out! An agent will be with you shortly.",
    offlineReply: "We are currently offline. We will respond as soon as possible.",
    delaySeconds: 1,
  }),
  businessHours: jsonb("business_hours").default(null),
  nicknamesEnabled: boolean("nicknames_enabled").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertLiveChatWidgetSchema = createInsertSchema(liveChatWidgets).omit({
  id: true,
  publicKey: true,
  createdAt: true,
});
export type InsertLiveChatWidget = z.infer<typeof insertLiveChatWidgetSchema>;
export type LiveChatWidget = typeof liveChatWidgets.$inferSelect;

export const liveChatWidgetAvailabilityOutbox = pgTable("live_chat_widget_availability_outbox", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  widgetId: varchar("widget_id").notNull(),
  publicWidgetId: varchar("public_widget_id").notNull(),
  active: boolean("active").notNull(),
  availabilityVersion: integer("availability_version").notNull(),
  availabilityUpdatedAt: timestamp("availability_updated_at").notNull(),
  status: text("status").notNull().default("pending"),
  attempts: integer("attempts").notNull().default(0),
  nextAttemptAt: timestamp("next_attempt_at"),
  lockedAt: timestamp("locked_at"),
  leaseExpiresAt: timestamp("lease_expires_at"),
  lockToken: text("lock_token"),
  lastError: text("last_error"),
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("live_chat_widget_availability_outbox_public_widget_version_unique")
    .on(table.publicWidgetId, table.availabilityVersion),
  index("live_chat_widget_availability_outbox_status_due_idx")
    .on(table.status, table.nextAttemptAt),
  index("live_chat_widget_availability_outbox_lease_idx")
    .on(table.status, table.leaseExpiresAt),
  index("live_chat_widget_availability_outbox_widget_idx")
    .on(table.widgetId),
]);

export const insertLiveChatWidgetAvailabilityOutboxSchema = createInsertSchema(liveChatWidgetAvailabilityOutbox).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertLiveChatWidgetAvailabilityOutbox = z.infer<typeof insertLiveChatWidgetAvailabilityOutboxSchema>;
export type LiveChatWidgetAvailabilityOutbox = typeof liveChatWidgetAvailabilityOutbox.$inferSelect;

export const liveChatWidgetAgents = pgTable("live_chat_widget_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  widgetId: varchar("widget_id").notNull(),
  userId: varchar("user_id").notNull(),
  nickname: text("nickname"),
});

export const insertLiveChatWidgetAgentSchema = createInsertSchema(liveChatWidgetAgents).omit({
  id: true,
});
export type InsertLiveChatWidgetAgent = z.infer<typeof insertLiveChatWidgetAgentSchema>;
export type LiveChatWidgetAgent = typeof liveChatWidgetAgents.$inferSelect;

export const liveChatVisitors = pgTable("live_chat_visitors", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  externalId: varchar("external_id").notNull(),
  name: text("name"),
  email: text("email"),
  phone: text("phone"),
  contactId: varchar("contact_id"),
  visitorNumber: integer("visitor_number"),
  metadata: jsonb("metadata").default({}),
  lastSeenAt: timestamp("last_seen_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertLiveChatVisitorSchema = createInsertSchema(liveChatVisitors).omit({
  id: true,
  createdAt: true,
});
export type InsertLiveChatVisitor = z.infer<typeof insertLiveChatVisitorSchema>;
export type LiveChatVisitor = typeof liveChatVisitors.$inferSelect;

export const liveChatSessionStatusEnum = pgEnum("live_chat_session_status", ["active", "ended", "abandoned"]);

export const liveChatSessions = pgTable("live_chat_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  widgetId: varchar("widget_id").notNull(),
  visitorId: varchar("visitor_id").notNull(),
  conversationId: varchar("conversation_id"),
  tenantId: varchar("tenant_id").notNull(),
  status: liveChatSessionStatusEnum("status").notNull().default("active"),
  ipAddress: text("ip_address"),
  startedAt: timestamp("started_at").defaultNow(),
  endedAt: timestamp("ended_at"),
  metadata: jsonb("metadata").default({}),
});

export const insertLiveChatSessionSchema = createInsertSchema(liveChatSessions).omit({
  id: true,
  startedAt: true,
  endedAt: true,
});
export type InsertLiveChatSession = z.infer<typeof insertLiveChatSessionSchema>;
export type LiveChatSession = typeof liveChatSessions.$inferSelect;

export const liveChatBlockedIps = pgTable("live_chat_blocked_ips", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  ipAddress: text("ip_address").notNull(),
  reason: text("reason"),
  blockedByUserId: varchar("blocked_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("live_chat_blocked_ips_tenant_ip_unique").on(table.tenantId, table.ipAddress),
]);

export const insertLiveChatBlockedIpSchema = createInsertSchema(liveChatBlockedIps).omit({
  id: true,
  createdAt: true,
});
export type InsertLiveChatBlockedIp = z.infer<typeof insertLiveChatBlockedIpSchema>;
export type LiveChatBlockedIp = typeof liveChatBlockedIps.$inferSelect;

export const autoReplyTriggerEnum = pgEnum("auto_reply_trigger", [
  "first_message",
  "offline",
  "outside_business_hours",
  "no_agent_available",
  "ticket_received",
  "ticket_resolved",
]);

export const autoReplies = pgTable("auto_replies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  featureType: text("feature_type").notNull(),
  entityId: varchar("entity_id"),
  triggerType: autoReplyTriggerEnum("trigger_type").notNull(),
  message: text("message").notNull(),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAutoReplySchema = createInsertSchema(autoReplies).omit({
  id: true,
  createdAt: true,
});
export type InsertAutoReply = z.infer<typeof insertAutoReplySchema>;
export type AutoReply = typeof autoReplies.$inferSelect;

export const liveChatRatings = pgTable("live_chat_ratings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull(),
  conversationId: varchar("conversation_id"),
  tenantId: varchar("tenant_id").notNull(),
  visitorId: varchar("visitor_id"),
  agentUserId: varchar("agent_user_id"),
  rating: integer("rating").notNull(),
  comment: text("comment"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertLiveChatRatingSchema = createInsertSchema(liveChatRatings).omit({
  id: true,
  createdAt: true,
});
export type InsertLiveChatRating = z.infer<typeof insertLiveChatRatingSchema>;
export type LiveChatRating = typeof liveChatRatings.$inferSelect;

export const crmPipelines = pgTable("crm_pipelines", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  isDefault: boolean("is_default").default(false),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertCrmPipelineSchema = createInsertSchema(crmPipelines).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCrmPipeline = z.infer<typeof insertCrmPipelineSchema>;
export type CrmPipeline = typeof crmPipelines.$inferSelect;

export const crmStages = pgTable("crm_stages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  pipelineId: varchar("pipeline_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  color: text("color").default("#6366f1"),
  sortOrder: integer("sort_order").default(0),
  isClosed: boolean("is_closed").default(false),
  isWon: boolean("is_won").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCrmStageSchema = createInsertSchema(crmStages).omit({
  id: true,
  createdAt: true,
});
export type InsertCrmStage = z.infer<typeof insertCrmStageSchema>;
export type CrmStage = typeof crmStages.$inferSelect;

export const crmDealStatusEnum = pgEnum("crm_deal_status", ["open", "won", "lost"]);

export const crmDeals = pgTable("crm_deals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  pipelineId: varchar("pipeline_id").notNull(),
  stageId: varchar("stage_id").notNull(),
  contactId: varchar("contact_id"),
  ownerId: varchar("owner_id"),
  title: text("title").notNull(),
  amount: numeric("amount", { precision: 12, scale: 2 }),
  currency: text("currency").default("USD"),
  status: crmDealStatusEnum("status").notNull().default("open"),
  probability: integer("probability").default(0),
  expectedCloseDate: timestamp("expected_close_date"),
  closedAt: timestamp("closed_at"),
  source: text("source"),
  description: text("description"),
  customFields: jsonb("custom_fields").default({}),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertCrmDealSchema = createInsertSchema(crmDeals).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCrmDeal = z.infer<typeof insertCrmDealSchema>;
export type CrmDeal = typeof crmDeals.$inferSelect;

export const crmDealActivityTypeEnum = pgEnum("crm_deal_activity_type", ["note", "stage_change", "status_change", "created", "updated", "email", "call", "sms", "meeting", "task", "system"]);
export const crmActivityStatusEnum = pgEnum("crm_activity_status", ["open", "completed", "canceled"]);

export const crmDealActivities = pgTable("crm_deal_activities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: varchar("deal_id"),
  contactId: varchar("contact_id"),
  tenantId: varchar("tenant_id").notNull(),
  type: crmDealActivityTypeEnum("type").notNull(),
  body: text("body"),
  metadata: jsonb("metadata").default({}),
  createdBy: varchar("created_by"),
  assignedTo: varchar("assigned_to"),
  status: crmActivityStatusEnum("status").default("open"),
  dueAt: timestamp("due_at"),
  reminderAt: timestamp("reminder_at"),
  completedAt: timestamp("completed_at"),
  source: varchar("source").default("manual"),
  externalRef: varchar("external_ref"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCrmDealActivitySchema = createInsertSchema(crmDealActivities).omit({
  id: true,
  createdAt: true,
  completedAt: true,
});
export type InsertCrmDealActivity = z.infer<typeof insertCrmDealActivitySchema>;
export type CrmDealActivity = typeof crmDealActivities.$inferSelect;

export const campaignWorkflows = pgTable("campaign_workflows", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  tenantWorkflowNumber: integer("tenant_workflow_number"),
  name: text("name").notNull(),
  welcomeMessageEnabled: boolean("welcome_message_enabled").default(false),
  welcomeMessageText: text("welcome_message_text"),
  welcomeMessageRecordingId: varchar("welcome_message_recording_id"),
  questions: jsonb("questions").default([]),
  endMessageEnabled: boolean("end_message_enabled").default(false),
  endMessageText: text("end_message_text"),
  endMessageRecordingId: varchar("end_message_recording_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCampaignWorkflowSchema = createInsertSchema(campaignWorkflows).omit({
  id: true,
  tenantWorkflowNumber: true,
  createdAt: true,
});
export type InsertCampaignWorkflow = z.infer<typeof insertCampaignWorkflowSchema>;
export type CampaignWorkflow = typeof campaignWorkflows.$inferSelect;

export const campaignStatusEnum = pgEnum("campaign_status", ["draft", "active", "paused", "completed", "archived"]);
export const campaignContactStatusEnum = pgEnum("campaign_contact_status", ["pending", "queued", "dialing", "connected", "completed", "skipped", "failed", "retry", "max_attempts", "dnc", "blocked"]);

export const campaigns = pgTable("campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  status: campaignStatusEnum("status").notNull().default("draft"),
  dialerType: text("dialer_type").notNull().default("power"),
  dialRatio: integer("dial_ratio").notNull().default(1),
  callerIdNumber: text("caller_id_number"),
  localPresenceEnabled: boolean("local_presence_enabled").default(false),
  amdEnabled: boolean("amd_enabled").default(false),
  amdAction: text("amd_action").default("hangup"),
  voicemailDropRecordingId: varchar("voicemail_drop_recording_id"),
  voicemailDropMaxDrops: integer("voicemail_drop_max_drops").default(0),
  considerVmAsSuccess: boolean("consider_vm_as_success").default(false),
  minCallDurationForAnswered: integer("min_call_duration_for_answered").default(0),
  maxAttemptsPerContact: integer("max_attempts_per_contact").default(3),
  retryDelayMinutes: integer("retry_delay_minutes").default(120),
  ongoing: boolean("ongoing").default(false),
  callingHoursStart: text("calling_hours_start").default("09:00"),
  callingHoursEnd: text("calling_hours_end").default("20:00"),
  callingHoursTimezone: text("calling_hours_timezone").default("America/New_York"),
  useContactTimezone: boolean("use_contact_timezone").notNull().default(true),
  excludeWeekends: boolean("exclude_weekends").default(false),
  saturdayEnabled: boolean("saturday_enabled").default(false),
  saturdayStart: text("saturday_start").default("09:00"),
  saturdayEnd: text("saturday_end").default("20:00"),
  sundayEnabled: boolean("sunday_enabled").default(false),
  sundayStart: text("sunday_start").default("09:00"),
  sundayEnd: text("sunday_end").default("20:00"),
  queueId: varchar("queue_id"),
  finalDispositionIds: text("final_disposition_ids").array().default(sql`'{}'::text[]`),
  enableCallScript: boolean("enable_call_script").default(false),
  callScriptId: varchar("call_script_id"),
  enableAutoDial: boolean("enable_auto_dial").default(false),
  useDefaultAgentCallerId: boolean("use_default_agent_caller_id").default(false),
  autoPickOutboundNumber: boolean("auto_pick_outbound_number").default(false),
  autoPickNumberIds: text("auto_pick_number_ids").array().default(sql`'{}'::text[]`),
  workflowId: varchar("workflow_id"),
  connectMessageEnabled: boolean("connect_message_enabled").default(false),
  connectMessageType: text("connect_message_type").default("tts"),
  connectMessageText: text("connect_message_text"),
  connectMessageRecordingId: varchar("connect_message_recording_id"),
  contactCallLimit: integer("contact_call_limit"),
  showNewContactsFirst: boolean("show_new_contacts_first").default(false),
  forceSkipReason: boolean("force_skip_reason").default(false),
  autoAdvanceAfterCall: boolean("auto_advance_after_call").default(true),
  dashboardTrackedDispositions: text("dashboard_tracked_dispositions").array().default(sql`'{}'::text[]`),
  totalContacts: integer("total_contacts").default(0),
  contactsDialed: integer("contacts_dialed").default(0),
  contactsReached: integer("contacts_reached").default(0),
  contactsRemaining: integer("contacts_remaining").default(0),
  abandonmentRate: numeric("abandonment_rate", { precision: 5, scale: 2 }).default("0"),
  blockListIds: text("block_list_ids").array(),
  createdBy: varchar("created_by"),
  tenantCampaignNumber: integer("tenant_campaign_number"),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCampaignSchema = createInsertSchema(campaigns).omit({
  id: true,
  totalContacts: true,
  contactsDialed: true,
  contactsReached: true,
  contactsRemaining: true,
  abandonmentRate: true,
  tenantCampaignNumber: true,
  startedAt: true,
  completedAt: true,
  createdAt: true,
});
export type InsertCampaign = z.infer<typeof insertCampaignSchema>;
export type Campaign = typeof campaigns.$inferSelect;

export const campaignLists = pgTable("campaign_lists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull(),
  listId: varchar("list_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("campaign_lists_unique_idx").on(table.campaignId, table.listId),
]);

export const insertCampaignListSchema = createInsertSchema(campaignLists).omit({
  id: true,
  createdAt: true,
});
export type InsertCampaignList = z.infer<typeof insertCampaignListSchema>;
export type CampaignList = typeof campaignLists.$inferSelect;

export const campaignAgents = pgTable("campaign_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull(),
  userId: varchar("user_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("campaign_agents_unique_idx").on(table.campaignId, table.userId),
]);

export const insertCampaignAgentSchema = createInsertSchema(campaignAgents).omit({
  id: true,
  createdAt: true,
});
export type InsertCampaignAgent = z.infer<typeof insertCampaignAgentSchema>;
export type CampaignAgent = typeof campaignAgents.$inferSelect;

export const campaignContacts = pgTable("campaign_contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  phoneNumber: text("phone_number").notNull(),
  status: campaignContactStatusEnum("status").notNull().default("pending"),
  priority: integer("priority").default(0),
  attempts: integer("attempts").default(0),
  lastAttemptAt: timestamp("last_attempt_at"),
  lastResult: text("last_result"),
  lastDisposition: text("last_disposition"),
  assignedAgentId: varchar("assigned_agent_id"),
  nextRetryAt: timestamp("next_retry_at"),
  callbackScheduledAt: timestamp("callback_scheduled_at"),
  completedAt: timestamp("completed_at"),
  skipReason: text("skip_reason"),
  timezone: text("timezone"),
  blockedCallRef: text("blocked_call_ref"),
  dateAddedToList: timestamp("date_added_to_list").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("campaign_contacts_unique_idx").on(table.campaignId, table.contactId, table.phoneNumber),
  index("campaign_contacts_status_idx").on(table.campaignId, table.status),
  index("campaign_contacts_next_retry_idx").on(table.campaignId, table.status, table.nextRetryAt),
  index("campaign_contacts_agent_idx").on(table.campaignId, table.assignedAgentId, table.status),
  index("idx_report_campaign_contacts_contact_latest").on(table.tenantId, table.contactId, table.dateAddedToList.desc(), table.id.desc()),
]);

export const insertCampaignContactSchema = createInsertSchema(campaignContacts).omit({
  id: true,
  attempts: true,
  lastAttemptAt: true,
  lastResult: true,
  lastDisposition: true,
  assignedAgentId: true,
  nextRetryAt: true,
  completedAt: true,
  createdAt: true,
});
export type InsertCampaignContact = z.infer<typeof insertCampaignContactSchema>;
export type CampaignContact = typeof campaignContacts.$inferSelect;

export const campaignCalls = pgTable("campaign_calls", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull(),
  campaignContactId: varchar("campaign_contact_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  agentId: varchar("agent_id"),
  callUuid: text("call_uuid"),
  fromNumber: text("from_number"),
  toNumber: text("to_number").notNull(),
  status: text("status").notNull().default("initiated"),
  duration: integer("duration").default(0),
  disposition: text("disposition"),
  dispositionNote: text("disposition_note"),
  isConnected: boolean("is_connected").default(false),
  isAbandoned: boolean("is_abandoned").default(false),
  amdResult: text("amd_result"),
  amdRoutingState: text("amd_routing_state"),
  recordingUrl: text("recording_url"),
  attemptNumber: integer("attempt_number").default(1),
  dialGroupId: text("dial_group_id"),
  callLogId: varchar("call_log_id"),
  startedAt: timestamp("started_at").defaultNow(),
  answeredAt: timestamp("answered_at"),
  endedAt: timestamp("ended_at"),
});

export const insertCampaignCallSchema = createInsertSchema(campaignCalls).omit({
  id: true,
  duration: true,
  isConnected: true,
  isAbandoned: true,
  amdResult: true,
  amdRoutingState: true,
  recordingUrl: true,
  answeredAt: true,
  endedAt: true,
  startedAt: true,
});
export type InsertCampaignCall = z.infer<typeof insertCampaignCallSchema>;
export type CampaignCall = typeof campaignCalls.$inferSelect;

export const webhookTriggerTypeEnum = pgEnum("webhook_trigger_type", [
  "call_ended",
  "call_disposition_saved",
  "call_disposition_modified",
  "contact_created",
  "contact_updated",
  "sms_received",
  "sms_sent",
  "deal_created",
  "deal_updated",
  "voicemail_received",
  "recording_ready",
]);

export const tenantWebhooks = pgTable("tenant_webhooks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  url: text("url").notNull(),
  triggerType: webhookTriggerTypeEnum("trigger_type").notNull(),
  waitForRecording: boolean("wait_for_recording").notNull().default(false),
  isActive: boolean("is_active").default(true),
  secret: text("secret"),
  headers: jsonb("headers").default({}),
  filterListId: varchar("filter_list_id"),
  filterDispositionId: varchar("filter_disposition_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTenantWebhookSchema = createInsertSchema(tenantWebhooks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).transform((webhook) => ({
  ...webhook,
  waitForRecording: webhook.triggerType === "call_ended" && webhook.waitForRecording === true,
}));
export type InsertTenantWebhook = z.infer<typeof insertTenantWebhookSchema>;
export type TenantWebhook = typeof tenantWebhooks.$inferSelect;

export const updateTenantWebhookSchema = createInsertSchema(tenantWebhooks).omit({
  id: true, tenantId: true, createdAt: true, updatedAt: true,
}).partial();

export function parseTenantWebhookUpdate(input: unknown, currentTrigger: TenantWebhook["triggerType"]) {
  const update = updateTenantWebhookSchema.parse(input);
  if ((update.triggerType ?? currentTrigger) !== "call_ended"
    || (update.triggerType && update.triggerType !== currentTrigger)) update.waitForRecording = false;
  return update;
}

export const callEndedWebhookDeliveries = pgTable("call_ended_webhook_deliveries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  webhookId: varchar("webhook_id").notNull(),
  callLogId: varchar("call_log_id").notNull(),
  storageContext: text("storage_context").notNull(),
  eventAt: timestamp("event_at", { withTimezone: true }).notNull(),
  deadlineAt: timestamp("deadline_at", { withTimezone: true }).notNull(),
  status: text("status").notNull().default("pending"),
  attempts: integer("attempts").notNull().default(0),
  nextAttemptAt: timestamp("next_attempt_at", { withTimezone: true }).notNull().defaultNow(),
  leaseExpiresAt: timestamp("lease_expires_at", { withTimezone: true }),
  lockToken: text("lock_token"),
  releaseReason: text("release_reason"),
  lastError: text("last_error"),
  completedAt: timestamp("completed_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  uniqueIndex("call_ended_webhook_delivery_unique").on(table.storageContext, table.webhookId, table.callLogId),
  index("idx_call_ended_delivery_tenant").on(table.tenantId),
  index("idx_call_ended_delivery_webhook").on(table.webhookId),
  index("idx_call_ended_delivery_call").on(table.callLogId),
  index("idx_call_ended_delivery_due").on(table.status, table.nextAttemptAt),
  index("idx_call_ended_delivery_deadline").on(table.status, table.deadlineAt),
  index("idx_call_ended_delivery_lease").on(table.status, table.leaseExpiresAt),
]);

export const webhookLogs = pgTable("webhook_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  webhookId: varchar("webhook_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  triggerType: text("trigger_type").notNull(),
  payload: jsonb("payload"),
  responseStatus: integer("response_status"),
  responseBody: text("response_body"),
  success: boolean("success").default(false),
  errorMessage: text("error_message"),
  attempt: integer("attempt").default(1),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertWebhookLogSchema = createInsertSchema(webhookLogs).omit({
  id: true,
  createdAt: true,
});
export type InsertWebhookLog = z.infer<typeof insertWebhookLogSchema>;
export type WebhookLog = typeof webhookLogs.$inferSelect;

export const supportChatStatusEnum = pgEnum("support_chat_status", ["active", "waiting", "closed"]);

export const supportChatSessions = pgTable("support_chat_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  tenantId: varchar("tenant_id"),
  assignedAdminId: varchar("assigned_admin_id"),
  status: supportChatStatusEnum("status").notNull().default("waiting"),
  subject: text("subject"),
  priority: text("priority").default("normal"),
  botHandled: boolean("bot_handled").default(false),
  escalatedAt: timestamp("escalated_at"),
  firstResponseAt: timestamp("first_response_at"),
  startedAt: timestamp("started_at").defaultNow(),
  endedAt: timestamp("ended_at"),
});

export const insertSupportChatSessionSchema = createInsertSchema(supportChatSessions).omit({
  id: true,
  firstResponseAt: true,
  escalatedAt: true,
  startedAt: true,
  endedAt: true,
});
export type InsertSupportChatSession = z.infer<typeof insertSupportChatSessionSchema>;
export type SupportChatSession = typeof supportChatSessions.$inferSelect;

export const supportChatMessages = pgTable("support_chat_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull(),
  senderId: varchar("sender_id").notNull(),
  senderRole: text("sender_role").notNull(),
  senderName: text("sender_name"),
  body: text("body").notNull(),
  isInternal: boolean("is_internal").default(false),
  attachments: jsonb("attachments").default([]),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSupportChatMessageSchema = createInsertSchema(supportChatMessages).omit({
  id: true,
  createdAt: true,
});
export type InsertSupportChatMessage = z.infer<typeof insertSupportChatMessageSchema>;
export type SupportChatMessage = typeof supportChatMessages.$inferSelect;

export const supportChatRatings = pgTable("support_chat_ratings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull(),
  userId: varchar("user_id").notNull(),
  rating: integer("rating").notNull(),
  comment: text("comment"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSupportChatRatingSchema = createInsertSchema(supportChatRatings).omit({
  id: true,
  createdAt: true,
});
export type InsertSupportChatRating = z.infer<typeof insertSupportChatRatingSchema>;
export type SupportChatRating = typeof supportChatRatings.$inferSelect;

export const supportSettings = pgTable("support_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  botEnabled: boolean("bot_enabled").default(false),
  botOutsideHoursEnabled: boolean("bot_outside_hours_enabled").default(false),
  botGreeting: text("bot_greeting"),
  botEscalationMessage: text("bot_escalation_message"),
  botInstructions: text("bot_instructions"),
  supportHoursStart: text("support_hours_start").default("09:00"),
  supportHoursEnd: text("support_hours_end").default("17:00"),
  supportDays: jsonb("support_days").default(["monday","tuesday","wednesday","thursday","friday"]),
  supportTimezone: text("support_timezone").default("America/New_York"),
  notifyEmail: boolean("notify_email").default(false),
  notifyEmailAddresses: jsonb("notify_email_addresses").default([]),
  notifySms: boolean("notify_sms").default(false),
  notifySmsNumbers: jsonb("notify_sms_numbers").default([]),
  notifySlack: boolean("notify_slack").default(false),
  slackBotToken: text("slack_bot_token"),
  slackChannel: text("slack_channel"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertSupportSettingsSchema = createInsertSchema(supportSettings).omit({
  id: true,
  updatedAt: true,
});
export type InsertSupportSettings = z.infer<typeof insertSupportSettingsSchema>;
export type SupportSettings = typeof supportSettings.$inferSelect;

export const cannedResponses = pgTable("canned_responses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  channel: text("channel").notNull(),
  channels: text("channels").array(),
  title: text("title").notNull(),
  body: text("body").notNull(),
  cc: text("cc"),
  bcc: text("bcc"),
  subject: text("subject"),
  inboxId: varchar("inbox_id"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCannedResponseSchema = createInsertSchema(cannedResponses).omit({
  id: true,
  createdAt: true,
});
export type InsertCannedResponse = z.infer<typeof insertCannedResponseSchema>;
export type CannedResponse = typeof cannedResponses.$inferSelect;

export const smsCampaigns = pgTable("sms_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  campaignNumber: integer("campaign_number"),
  name: text("name").notNull(),
  message: text("message").notNull(),
  fromNumber: text("from_number").notNull(),
  status: text("status").notNull().default("draft"),
  sendSpeed: integer("send_speed").notNull().default(1),
  ongoing: boolean("ongoing").notNull().default(false),
  autoPickOutbound: boolean("auto_pick_outbound").notNull().default(false),
  outboundNumbers: text("outbound_numbers").array(),
  powerpackId: text("powerpack_id"),
  scheduledAt: timestamp("scheduled_at"),
  overrideBusinessHours: boolean("override_business_hours").notNull().default(false),
  businessHoursSchedule: jsonb("business_hours_schedule"),
  blockListIds: text("block_list_ids").array(),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  totalContacts: integer("total_contacts").default(0),
  sentCount: integer("sent_count").default(0),
  failedCount: integer("failed_count").default(0),
  blockedCount: integer("blocked_count").default(0),
  deliveredCount: integer("delivered_count").default(0),
  undeliveredCount: integer("undelivered_count").default(0),
  aiBotHandleReplies: boolean("ai_bot_handle_replies").notNull().default(false),
  aiBotContext: text("ai_bot_context"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  preparationError: text("preparation_error"),
}, (table) => [
  index("idx_sms_campaigns_tenant_status_scheduled").on(table.tenantId, table.status, table.scheduledAt),
  index("idx_sms_campaigns_tenant_created").on(table.tenantId, table.createdAt),
]);

export const insertSmsCampaignSchema = createInsertSchema(smsCampaigns).omit({
  id: true,
  campaignNumber: true,
  sentCount: true,
  failedCount: true,
  blockedCount: true,
  deliveredCount: true,
  undeliveredCount: true,
  startedAt: true,
  completedAt: true,
  createdAt: true,
  updatedAt: true,
  preparationError: true,
});
export type InsertSmsCampaign = z.infer<typeof insertSmsCampaignSchema>;
export type SmsCampaign = typeof smsCampaigns.$inferSelect;

export const smsCampaignLists = pgTable("sms_campaign_lists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull(),
  contactListId: varchar("contact_list_id").notNull(),
}, (table) => [
  uniqueIndex("sms_campaign_lists_unique_idx").on(table.campaignId, table.contactListId),
]);

export const insertSmsCampaignListSchema = createInsertSchema(smsCampaignLists).omit({
  id: true,
});
export type InsertSmsCampaignList = z.infer<typeof insertSmsCampaignListSchema>;
export type SmsCampaignList = typeof smsCampaignLists.$inferSelect;

export const smsCampaignMessages = pgTable("sms_campaign_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  phoneNumber: text("phone_number").notNull(),
  message: text("message").notNull(),
  status: text("status").notNull().default("pending"),
  fromNumber: text("from_number"),
  plivoMessageUuid: text("plivo_message_uuid"),
  errorMessage: text("error_message"),
  sentAt: timestamp("sent_at"),
  deliveredAt: timestamp("delivered_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_sms_campaign_messages_campaign_status_created").on(table.campaignId, table.status, table.createdAt),
  index("idx_sms_campaign_messages_campaign_contact").on(table.campaignId, table.contactId),
  index("idx_sms_campaign_messages_plivo_uuid").on(table.plivoMessageUuid),
]);

export const insertSmsCampaignMessageSchema = createInsertSchema(smsCampaignMessages).omit({
  id: true,
  plivoMessageUuid: true,
  errorMessage: true,
  sentAt: true,
  deliveredAt: true,
  createdAt: true,
});
export type InsertSmsCampaignMessage = z.infer<typeof insertSmsCampaignMessageSchema>;
export type SmsCampaignMessage = typeof smsCampaignMessages.$inferSelect;

export const dialerScripts = pgTable("dialer_scripts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  content: text("content").notNull().default(""),
  scriptType: text("script_type").$type<"inbound" | "outbound" | "both">().default("both"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertDialerScriptSchema = createInsertSchema(dialerScripts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertDialerScript = z.infer<typeof insertDialerScriptSchema>;
export type DialerScript = typeof dialerScripts.$inferSelect;

export const screenPopScripts = pgTable("screen_pop_scripts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  content: text("content").notNull().default(""),
  scriptType: text("script_type").$type<"inbound" | "outbound" | "both" | "campaign">().default("both"),
  queueId: varchar("queue_id"),
  phoneNumbers: text("phone_numbers").array(),
  isActive: boolean("is_active").default(true),
  legacyId: text("legacy_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertScreenPopScriptSchema = createInsertSchema(screenPopScripts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertScreenPopScript = z.infer<typeof insertScreenPopScriptSchema>;
export type ScreenPopScript = typeof screenPopScripts.$inferSelect;

export const screenPopExternalLinks = pgTable("screen_pop_external_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  label: text("label").notNull(),
  url: text("url").notNull(),
  description: text("description"),
  isActive: boolean("is_active").default(true),
  openInIframe: boolean("open_in_iframe").default(true),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertScreenPopExternalLinkSchema = createInsertSchema(screenPopExternalLinks).omit({
  id: true,
  createdAt: true,
});
export type InsertScreenPopExternalLink = z.infer<typeof insertScreenPopExternalLinkSchema>;
export type ScreenPopExternalLink = typeof screenPopExternalLinks.$inferSelect;

export const whatsappSettings = pgTable("whatsapp_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  whatsappNumber: text("whatsapp_number").notNull(),
  displayName: text("display_name"),
  twilioAccountSid: text("twilio_account_sid"),
  twilioAuthToken: text("twilio_auth_token"),
  webhookUrl: text("webhook_url"),
  enabled: boolean("enabled").default(true),
  facebookPageId: text("facebook_page_id"),
  facebookPageName: text("facebook_page_name"),
  facebookEnabled: boolean("facebook_enabled").default(false),
  instagramAccountId: text("instagram_account_id"),
  instagramAccountName: text("instagram_account_name"),
  instagramEnabled: boolean("instagram_enabled").default(false),
  messagebirdApiKey: text("messagebird_api_key"),
  messagebirdChannelId: text("messagebird_channel_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertWhatsappSettingsSchema = createInsertSchema(whatsappSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertWhatsappSettings = z.infer<typeof insertWhatsappSettingsSchema>;
export type WhatsappSettings = typeof whatsappSettings.$inferSelect;

export const whatsappTemplates = pgTable("whatsapp_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  body: text("body").notNull(),
  category: text("category").$type<"greeting" | "followup" | "support" | "marketing" | "other">().default("other"),
  isActive: boolean("is_active").default(true),
  contentSid: text("content_sid"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertWhatsappTemplateSchema = createInsertSchema(whatsappTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertWhatsappTemplate = z.infer<typeof insertWhatsappTemplateSchema>;
export type WhatsappTemplate = typeof whatsappTemplates.$inferSelect;

export const whatsappAutoReplies = pgTable("whatsapp_auto_replies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  triggerType: text("trigger_type").$type<"first_message" | "offline" | "keyword">().notNull(),
  keyword: text("keyword"),
  replyBody: text("reply_body").notNull(),
  delaySeconds: integer("delay_seconds").default(0),
  enabled: boolean("enabled").default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertWhatsappAutoReplySchema = createInsertSchema(whatsappAutoReplies).omit({
  id: true,
  createdAt: true,
});
export type InsertWhatsappAutoReply = z.infer<typeof insertWhatsappAutoReplySchema>;
export type WhatsappAutoReply = typeof whatsappAutoReplies.$inferSelect;

export const whatsappAgents = pgTable("whatsapp_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  userId: varchar("user_id").notNull(),
});

export const insertWhatsappAgentSchema = createInsertSchema(whatsappAgents).omit({
  id: true,
});
export type InsertWhatsappAgent = z.infer<typeof insertWhatsappAgentSchema>;
export type WhatsappAgent = typeof whatsappAgents.$inferSelect;

export const facebookAgents = pgTable("facebook_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  userId: varchar("user_id").notNull(),
});

export const insertFacebookAgentSchema = createInsertSchema(facebookAgents).omit({
  id: true,
});
export type InsertFacebookAgent = z.infer<typeof insertFacebookAgentSchema>;
export type FacebookAgent = typeof facebookAgents.$inferSelect;

export const instagramAgents = pgTable("instagram_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  userId: varchar("user_id").notNull(),
});

export const insertInstagramAgentSchema = createInsertSchema(instagramAgents).omit({
  id: true,
});
export type InsertInstagramAgent = z.infer<typeof insertInstagramAgentSchema>;
export type InstagramAgent = typeof instagramAgents.$inferSelect;

export const sipForwardingSettings = pgTable("sip_forwarding_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  enabled: boolean("enabled").default(false),
  sipDestination: text("sip_destination").notNull(),
  sipTransport: text("sip_transport").$type<"udp" | "tcp" | "tls">().default("udp"),
  sendCallerIdHeader: boolean("send_caller_id_header").default(true),
  sendFromHeader: boolean("send_from_header").default(true),
  sendToHeader: boolean("send_to_header").default(true),
  customHeaders: text("custom_headers"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertSipForwardingSettingsSchema = createInsertSchema(sipForwardingSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertSipForwardingSettings = z.infer<typeof insertSipForwardingSettingsSchema>;
export type SipForwardingSettings = typeof sipForwardingSettings.$inferSelect;

export const loadTestRunStatusEnum = pgEnum("load_test_run_status", ["pending", "running", "completed", "failed", "cancelled"]);

export const loadTestRuns = pgTable("load_test_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  status: loadTestRunStatusEnum("status").default("pending").notNull(),
  testType: text("test_type").notNull(),
  config: jsonb("config").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  progress: integer("progress").default(0),
  total: integer("total").default(0),
  results: jsonb("results"),
  error: text("error"),
  createdContactIds: text("created_contact_ids").array().default(sql`'{}'`),
  createdCallLogIds: text("created_call_log_ids").array().default(sql`'{}'`),
  createdCampaignIds: text("created_campaign_ids").array().default(sql`'{}'`),
  createdMessageIds: text("created_message_ids").array().default(sql`'{}'`),
  createdDealIds: text("created_deal_ids").array().default(sql`'{}'`),
  createdTicketIds: text("created_ticket_ids").array().default(sql`'{}'`),
  createdChatSessionIds: text("created_chat_session_ids").array().default(sql`'{}'`),
  startedAt: timestamp("started_at"),
  finishedAt: timestamp("finished_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertLoadTestRunSchema = createInsertSchema(loadTestRuns).omit({
  id: true,
  createdAt: true,
});
export type InsertLoadTestRun = z.infer<typeof insertLoadTestRunSchema>;
export type LoadTestRun = typeof loadTestRuns.$inferSelect;

export const crmIntegrations = pgTable("crm_integrations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  provider: text("provider").notNull(),
  apiKey: text("api_key").notNull(),
  apiBaseUrl: text("api_base_url"),
  isActive: boolean("is_active").default(false),
  status: text("status").default("disconnected"),
  logCalls: boolean("log_calls").default(true),
  logNotes: boolean("log_notes").default(true),
  logRecordings: boolean("log_recordings").default(true),
  syncContacts: boolean("sync_contacts").default(true),
  syncDirection: text("sync_direction").default("both"),
  contactSyncIntervalHours: integer("contact_sync_interval_hours").default(3),
  lastSyncAt: timestamp("last_sync_at"),
  lastError: text("last_error"),
  shopifyClientId: text("shopify_client_id"),
  shopifyClientSecret: text("shopify_client_secret"),
  hubspotTwoWaySync: boolean("hubspot_two_way_sync").default(false),
  keyCrmTwoWaySync: boolean("keycrm_two_way_sync").default(false),
  lastPushAt: timestamp("last_push_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertCrmIntegrationSchema = createInsertSchema(crmIntegrations).omit({
  id: true,
  lastSyncAt: true,
  lastError: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCrmIntegration = z.infer<typeof insertCrmIntegrationSchema>;
export type CrmIntegration = typeof crmIntegrations.$inferSelect;

export const crmSyncLogs = pgTable("crm_sync_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  integrationId: varchar("integration_id").notNull(),
  eventType: text("event_type").notNull(),
  callLogId: varchar("call_log_id"),
  contactId: varchar("contact_id"),
  direction: text("direction"),
  status: text("status").notNull().default("pending"),
  errorMessage: text("error_message"),
  requestPayload: jsonb("request_payload"),
  responsePayload: jsonb("response_payload"),
  crmContactId: text("crm_contact_id"),
  crmActivityId: text("crm_activity_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCrmSyncLogSchema = createInsertSchema(crmSyncLogs).omit({
  id: true,
  createdAt: true,
});
export type InsertCrmSyncLog = z.infer<typeof insertCrmSyncLogSchema>;
export type CrmSyncLog = typeof crmSyncLogs.$inferSelect;

export const integrationConnections = pgTable("integration_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  providerKey: text("provider_key").$type<IntegrationProviderKey>().notNull(),
  category: text("category").$type<IntegrationCategory>().notNull(),
  authMode: text("auth_mode"),
  enabled: boolean("enabled").notNull().default(false),
  status: text("status").$type<IntegrationConnectionStatus>().notNull().default("disconnected"),
  config: jsonb("config").$type<Record<string, unknown>>().notNull().default({}),
  encryptedCredentials: text("encrypted_credentials"),
  credentialMetadata: jsonb("credential_metadata").$type<{
    configuredFields: string[];
    maskedSuffixes: Record<string, string>;
  }>().notNull().default({ configuredFields: [], maskedSuffixes: {} }),
  lastContactSyncAt: timestamp("last_contact_sync_at"),
  lastEventAt: timestamp("last_event_at"),
  lastTestedAt: timestamp("last_tested_at"),
  lastSuccessAt: timestamp("last_success_at"),
  errorCode: text("error_code"),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("integration_connections_tenant_provider_unique").on(table.tenantId, table.providerKey),
  index("integration_connections_tenant_idx").on(table.tenantId),
  index("integration_connections_status_idx").on(table.status, table.enabled),
]);

export const integrationEntityLinks = pgTable("integration_entity_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  connectionId: varchar("connection_id").notNull().references(() => integrationConnections.id, { onDelete: "cascade" }),
  localEntityType: text("local_entity_type").notNull(),
  localEntityId: varchar("local_entity_id").notNull(),
  remoteEntityType: text("remote_entity_type").notNull(),
  remoteEntityId: text("remote_entity_id").notNull(),
  remoteUrl: text("remote_url"),
  metadata: jsonb("metadata").$type<Record<string, unknown>>().notNull().default({}),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("integration_entity_links_mapping_unique").on(
    table.connectionId,
    table.localEntityType,
    table.localEntityId,
    table.remoteEntityType,
  ),
  index("integration_entity_links_tenant_local_idx").on(table.tenantId, table.localEntityType, table.localEntityId),
  index("integration_entity_links_tenant_remote_idx").on(table.tenantId, table.remoteEntityType, table.remoteEntityId),
]);

export const integrationOperations = pgTable("integration_operations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  connectionId: varchar("connection_id").notNull().references(() => integrationConnections.id, { onDelete: "cascade" }),
  providerKey: text("provider_key").$type<IntegrationProviderKey>().notNull(),
  operationType: text("operation_type").$type<IntegrationOperationType>().notNull(),
  trigger: text("trigger").$type<IntegrationOperationTrigger>().notNull(),
  status: text("status").$type<IntegrationOperationStatus>().notNull().default("queued"),
  encryptedPayload: text("encrypted_payload"),
  payloadExpiresAt: timestamp("payload_expires_at"),
  idempotencyKey: text("idempotency_key").notNull(),
  attempt: integer("attempt").notNull().default(0),
  maxAttempts: integer("max_attempts").notNull().default(5),
  progress: jsonb("progress").$type<IntegrationOperationProgress>().notNull().default({
    current: 0,
    total: 0,
    imported: 0,
    updated: 0,
    skipped: 0,
    failed: 0,
  }),
  resultSummary: jsonb("result_summary").$type<Record<string, unknown>>(),
  leaseOwner: text("lease_owner"),
  leaseExpiresAt: timestamp("lease_expires_at"),
  dueAt: timestamp("due_at").notNull().defaultNow(),
  errorClassification: text("error_classification"),
  errorCode: text("error_code"),
  errorMessage: text("error_message"),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("integration_operations_idempotency_unique").on(table.idempotencyKey),
  index("integration_operations_claim_idx").on(table.status, table.dueAt, table.leaseExpiresAt),
  index("integration_operations_connection_history_idx").on(table.connectionId, table.createdAt),
  index("integration_operations_tenant_history_idx").on(table.tenantId, table.createdAt),
]);

export type IntegrationConnection = typeof integrationConnections.$inferSelect;
export type IntegrationEntityLink = typeof integrationEntityLinks.$inferSelect;
export type IntegrationOperation = typeof integrationOperations.$inferSelect;

export const automationStatusEnum = pgEnum("automation_status", ["active", "inactive", "draft"]);
export const automationEnrollmentStatusEnum = pgEnum("automation_enrollment_status", ["active", "completed", "cancelled", "paused", "failed"]);
export const automationQueueStatusEnum = pgEnum("automation_queue_status", ["pending", "executing", "executed", "failed", "skipped"]);

export const automations = pgTable("automations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  status: automationStatusEnum("status").notNull().default("draft"),
  triggerType: text("trigger_type"),
  triggerConfig: jsonb("trigger_config").default({}),
  flowDefinition: jsonb("flow_definition").default({}),
  allowReenrollment: boolean("allow_reenrollment").default(false),
  createdBy: varchar("created_by"),
  enrollmentCount: integer("enrollment_count").default(0),
  lastRunAt: timestamp("last_run_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertAutomationSchema = createInsertSchema(automations).omit({
  id: true,
  enrollmentCount: true,
  lastRunAt: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertAutomation = z.infer<typeof insertAutomationSchema>;
export type Automation = typeof automations.$inferSelect;

export const automationEnrollments = pgTable("automation_enrollments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  automationId: varchar("automation_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  currentNodeId: text("current_node_id"),
  status: automationEnrollmentStatusEnum("status").notNull().default("active"),
  context: jsonb("context").default({}),
  enrolledAt: timestamp("enrolled_at").defaultNow(),
  completedAt: timestamp("completed_at"),
}, (table) => [
  index("idx_automation_enrollments_automation_contact").on(table.automationId, table.contactId, table.status),
]);

export const insertAutomationEnrollmentSchema = createInsertSchema(automationEnrollments).omit({
  id: true,
  enrolledAt: true,
  completedAt: true,
});
export type InsertAutomationEnrollment = z.infer<typeof insertAutomationEnrollmentSchema>;
export type AutomationEnrollment = typeof automationEnrollments.$inferSelect;

export const automationQueue = pgTable("automation_queue", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  enrollmentId: varchar("enrollment_id").notNull(),
  automationId: varchar("automation_id").notNull(),
  nodeId: text("node_id").notNull(),
  actionType: text("action_type").notNull(),
  payload: jsonb("payload").default({}),
  status: automationQueueStatusEnum("status").notNull().default("pending"),
  scheduledAt: timestamp("scheduled_at").notNull(),
  executedAt: timestamp("executed_at"),
  errorMessage: text("error_message"),
  retryCount: integer("retry_count").default(0),
  maxRetries: integer("max_retries").default(3),
});

export const insertAutomationQueueSchema = createInsertSchema(automationQueue).omit({
  id: true,
  executedAt: true,
});
export type InsertAutomationQueue = z.infer<typeof insertAutomationQueueSchema>;
export type AutomationQueueItem = typeof automationQueue.$inferSelect;

export const automationLogs = pgTable("automation_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  automationId: varchar("automation_id").notNull(),
  enrollmentId: varchar("enrollment_id"),
  contactId: varchar("contact_id"),
  nodeId: text("node_id"),
  actionType: text("action_type").notNull(),
  status: text("status").notNull(),
  details: jsonb("details").default({}),
  errorMessage: text("error_message"),
  executedAt: timestamp("executed_at").defaultNow(),
});

export const insertAutomationLogSchema = createInsertSchema(automationLogs).omit({
  id: true,
  executedAt: true,
});
export type InsertAutomationLog = z.infer<typeof insertAutomationLogSchema>;
export type AutomationLog = typeof automationLogs.$inferSelect;

export const importHistory = pgTable("import_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  listId: varchar("list_id").notNull(),
  fileName: text("file_name"),
  totalRows: integer("total_rows").default(0),
  imported: integer("imported").default(0),
  skipped: integer("skipped").default(0),
  updated: integer("updated").default(0),
  duplicatesFound: integer("duplicates_found").default(0),
  errors: jsonb("errors").default([]),
  status: text("status").notNull().default("processing"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertImportHistorySchema = createInsertSchema(importHistory).omit({
  id: true,
  createdAt: true,
});
export type InsertImportHistory = z.infer<typeof insertImportHistorySchema>;
export type ImportHistory = typeof importHistory.$inferSelect;

export const savedReports = pgTable("saved_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  createdBy: varchar("created_by").notNull(),
  name: varchar("name").notNull(),
  description: text("description"),
  category: varchar("category").notNull(),
  reportType: varchar("report_type").notNull(),
  definition: jsonb("definition").notNull().default({}),
  isFavorite: boolean("is_favorite").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertSavedReportSchema = createInsertSchema(savedReports).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertSavedReport = z.infer<typeof insertSavedReportSchema>;
export type SavedReport = typeof savedReports.$inferSelect;

export const scheduledReports = pgTable("scheduled_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  savedReportId: varchar("saved_report_id").notNull(),
  createdBy: varchar("created_by").notNull(),
  frequency: varchar("frequency").notNull(),
  deliveryTime: varchar("delivery_time").notNull(),
  timezone: varchar("timezone").notNull().default("America/New_York"),
  recipientEmails: text("recipient_emails").array().notNull(),
  isActive: boolean("is_active").default(true),
  lastRunAt: timestamp("last_run_at"),
  nextRunAt: timestamp("next_run_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertScheduledReportSchema = createInsertSchema(scheduledReports).omit({
  id: true,
  lastRunAt: true,
  nextRunAt: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertScheduledReport = z.infer<typeof insertScheduledReportSchema>;
export type ScheduledReport = typeof scheduledReports.$inferSelect;

export const activeTransferSessions = pgTable("active_transfer_sessions", {
  id: varchar("id").primaryKey(),
  tenantId: varchar("tenant_id").notNull(),
  sessionData: jsonb("session_data").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const knowledgeBases = pgTable("knowledge_bases", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertKnowledgeBaseSchema = createInsertSchema(knowledgeBases).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertKnowledgeBase = z.infer<typeof insertKnowledgeBaseSchema>;
export type KnowledgeBase = typeof knowledgeBases.$inferSelect;

export const kbCategories = pgTable("kb_categories", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  knowledgeBaseId: varchar("knowledge_base_id").notNull(),
  name: text("name").notNull(),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertKbCategorySchema = createInsertSchema(kbCategories).omit({
  id: true,
  createdAt: true,
});
export type InsertKbCategory = z.infer<typeof insertKbCategorySchema>;
export type KbCategory = typeof kbCategories.$inferSelect;

export const kbArticles = pgTable("kb_articles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  knowledgeBaseId: varchar("knowledge_base_id").notNull(),
  categoryId: varchar("category_id"),
  title: text("title").notNull(),
  content: text("content").notNull(),
  status: varchar("status").notNull().default("draft"),
  sourceType: varchar("source_type").notNull(),
  sourceRef: text("source_ref"),
  searchKeywords: text("search_keywords"),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertKbArticleSchema = createInsertSchema(kbArticles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertKbArticle = z.infer<typeof insertKbArticleSchema>;
export type KbArticle = typeof kbArticles.$inferSelect;

export const kbArticleFeedback = pgTable("kb_article_feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  articleId: varchar("article_id").notNull(),
  userId: varchar("user_id").notNull(),
  isHelpful: boolean("is_helpful").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertKbArticleFeedbackSchema = createInsertSchema(kbArticleFeedback).omit({
  id: true,
  createdAt: true,
});
export type InsertKbArticleFeedback = z.infer<typeof insertKbArticleFeedbackSchema>;
export type KbArticleFeedback = typeof kbArticleFeedback.$inferSelect;

export const notificationCategoryEnum = pgEnum("notification_category", [
  "queue_service_level",
  "agent_workforce",
  "call_quality_compliance",
  "system_technical",
  "supervisor_escalation",
  "scheduled_digest",
]);

export const notificationPriorityEnum = pgEnum("notification_priority", [
  "low",
  "medium",
  "high",
  "critical",
]);

export const notificationChannelEnum = pgEnum("notification_channel", [
  "email",
  "sms",
]);

export const notificationGlobalSettings = pgTable("notification_global_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  masterEnabled: boolean("master_enabled").notNull().default(true),
  quietHoursStart: text("quiet_hours_start"),
  quietHoursEnd: text("quiet_hours_end"),
  quietHoursOverrideCritical: boolean("quiet_hours_override_critical").notNull().default(true),
  maxAlertsPerHour: integer("max_alerts_per_hour").notNull().default(20),
  dedupWindowSeconds: integer("dedup_window_seconds").notNull().default(300),
  escalationDelayMin: integer("escalation_delay_min").notNull().default(5),
  timezone: text("timezone").notNull().default("UTC"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertNotificationGlobalSettingsSchema = createInsertSchema(notificationGlobalSettings).omit({
  id: true,
  updatedAt: true,
});
export type InsertNotificationGlobalSettings = z.infer<typeof insertNotificationGlobalSettingsSchema>;
export type NotificationGlobalSettings = typeof notificationGlobalSettings.$inferSelect;

export const notificationConfigs = pgTable("notification_configs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  category: notificationCategoryEnum("category").notNull(),
  notificationType: text("notification_type").notNull(),
  displayName: text("display_name").notNull(),
  description: text("description"),
  enabled: boolean("enabled").notNull().default(false),
  channels: text("channels").array().notNull().default(sql`ARRAY['email']::text[]`),
  emailRecipients: text("email_recipients").array().notNull().default(sql`ARRAY[]::text[]`),
  smsRecipients: text("sms_recipients").array().notNull().default(sql`ARRAY[]::text[]`),
  threshold: numeric("threshold"),
  thresholdUnit: text("threshold_unit"),
  evaluationWindowSeconds: integer("evaluation_window_seconds").notNull().default(60),
  cooldownMinutes: integer("cooldown_minutes").notNull().default(15),
  priority: notificationPriorityEnum("priority").notNull().default("medium"),
  subjectTemplate: text("subject_template"),
  bodyTemplate: text("body_template"),
  smsTemplate: text("sms_template"),
  autoResolve: boolean("auto_resolve").notNull().default(false),
  webhookUrl: text("webhook_url"),
  tags: text("tags").array().notNull().default(sql`ARRAY[]::text[]`),
  isAvailable: boolean("is_available").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertNotificationConfigSchema = createInsertSchema(notificationConfigs).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertNotificationConfig = z.infer<typeof insertNotificationConfigSchema>;
export type NotificationConfig = typeof notificationConfigs.$inferSelect;

export const notificationAlertHistory = pgTable("notification_alert_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  configId: varchar("config_id"),
  notificationType: text("notification_type").notNull(),
  category: text("category").notNull(),
  channel: text("channel").notNull(),
  recipient: text("recipient").notNull(),
  subject: text("subject"),
  body: text("body"),
  currentValue: numeric("current_value"),
  thresholdValue: numeric("threshold_value"),
  dedupKey: text("dedup_key"),
  status: text("status").notNull().default("sent"),
  error: text("error"),
  triggeredAt: timestamp("triggered_at").defaultNow(),
  deliveredAt: timestamp("delivered_at"),
  resolvedAt: timestamp("resolved_at"),
});

export const insertNotificationAlertHistorySchema = createInsertSchema(notificationAlertHistory).omit({
  id: true,
  triggeredAt: true,
});
export type InsertNotificationAlertHistory = z.infer<typeof insertNotificationAlertHistorySchema>;
export type NotificationAlertHistory = typeof notificationAlertHistory.$inferSelect;

export const teamChatChannels = pgTable("team_chat_channels", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull(),
  name: text("name"),
  type: text("type").notNull().default("direct"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertTeamChatChannelSchema = createInsertSchema(teamChatChannels).omit({
  id: true,
  createdAt: true,
});
export type InsertTeamChatChannel = z.infer<typeof insertTeamChatChannelSchema>;
export type TeamChatChannel = typeof teamChatChannels.$inferSelect;

export const teamChatMembers = pgTable("team_chat_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  channelId: text("channel_id").notNull(),
  userId: text("user_id").notNull(),
  joinedAt: timestamp("joined_at").defaultNow(),
  lastReadAt: timestamp("last_read_at"),
});

export const insertTeamChatMemberSchema = createInsertSchema(teamChatMembers).omit({
  id: true,
  joinedAt: true,
});
export type InsertTeamChatMember = z.infer<typeof insertTeamChatMemberSchema>;
export type TeamChatMember = typeof teamChatMembers.$inferSelect;

export const teamChatMessages = pgTable("team_chat_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  channelId: text("channel_id").notNull(),
  senderUserId: text("sender_user_id").notNull(),
  body: text("body").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  editedAt: timestamp("edited_at"),
  isDeleted: boolean("is_deleted").default(false),
});

export const insertTeamChatMessageSchema = createInsertSchema(teamChatMessages).omit({
  id: true,
  createdAt: true,
});
export type InsertTeamChatMessage = z.infer<typeof insertTeamChatMessageSchema>;
export type TeamChatMessage = typeof teamChatMessages.$inferSelect;

export const apiKeys = pgTable("api_keys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  keyPrefix: text("key_prefix").notNull(),
  hashedKey: text("hashed_key").notNull(),
  scopes: text("scopes").array().default(sql`ARRAY['all']::text[]`),
  lastUsedAt: timestamp("last_used_at"),
  createdAt: timestamp("created_at").defaultNow(),
  revokedAt: timestamp("revoked_at"),
});

export const insertApiKeySchema = createInsertSchema(apiKeys).omit({
  id: true,
  lastUsedAt: true,
  createdAt: true,
  revokedAt: true,
});
export type InsertApiKey = z.infer<typeof insertApiKeySchema>;
export type ApiKey = typeof apiKeys.$inferSelect;

export const WEBHOOK_TRIGGER_TYPES = [
  "call_ended",
  "call_disposition_saved",
  "call_disposition_modified",
  "contact_created",
  "contact_updated",
  "sms_received",
  "sms_sent",
  "deal_created",
  "deal_updated",
  "voicemail_received",
  "recording_ready",
] as const;

export type WebhookTriggerType = typeof WEBHOOK_TRIGGER_TYPES[number];

export const aiBotActions = pgTable("ai_bot_actions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  type: text("type").notNull(),
  name: text("name").notNull(),
  description: text("description").notNull(),
  isEnabled: boolean("is_enabled").notNull().default(true),
  config: jsonb("config").default({}),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("ai_bot_actions_tenant_idx").on(table.tenantId)]);

export const insertAiBotActionSchema = createInsertSchema(aiBotActions).omit({
  id: true,
  createdAt: true,
});
export type InsertAiBotAction = z.infer<typeof insertAiBotActionSchema>;
export type AiBotAction = typeof aiBotActions.$inferSelect;

export const aiBotActionLogs = pgTable("ai_bot_action_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  actionId: varchar("action_id"),
  conversationId: varchar("conversation_id"),
  actionType: text("action_type").notNull(),
  actionName: text("action_name"),
  input: jsonb("input"),
  output: jsonb("output"),
  success: boolean("success").notNull().default(true),
  errorMessage: text("error_message"),
  executedAt: timestamp("executed_at").defaultNow(),
}, (table) => [
  index("ai_bot_action_logs_tenant_action_idx").on(table.tenantId, table.actionId, table.executedAt),
]);

export const insertAiBotActionLogSchema = createInsertSchema(aiBotActionLogs).omit({
  id: true,
  executedAt: true,
});
export type InsertAiBotActionLog = z.infer<typeof insertAiBotActionLogSchema>;
export type AiBotActionLog = typeof aiBotActionLogs.$inferSelect;

export const aiBotDailySummaries = pgTable("ai_bot_daily_summaries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  summaryDate: text("summary_date").notNull(),
  totalConversations: integer("total_conversations").default(0),
  duringHoursCount: integer("during_hours_count").default(0),
  afterHoursCount: integer("after_hours_count").default(0),
  resolvedCount: integer("resolved_count").default(0),
  handedOffCount: integer("handed_off_count").default(0),
  actionsExecuted: jsonb("actions_executed").default({}),
  summaryText: text("summary_text"),
  emailSentAt: timestamp("email_sent_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAiBotDailySummarySchema = createInsertSchema(aiBotDailySummaries).omit({
  id: true,
  createdAt: true,
});
export type InsertAiBotDailySummary = z.infer<typeof insertAiBotDailySummarySchema>;
export type AiBotDailySummary = typeof aiBotDailySummaries.$inferSelect;

export const aiBotAutoResolveTriggers = pgTable("ai_bot_auto_resolve_triggers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  position: integer("position").notNull().default(0),
  delayMinutes: integer("delay_minutes").notNull(),
  channels: text("channels").array().notNull(),
  message: text("message"),
  closeConversation: boolean("close_conversation").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_ai_bot_auto_resolve_triggers_tenant_position").on(table.tenantId, table.position),
]);

export const insertAiBotAutoResolveTriggerSchema = createInsertSchema(aiBotAutoResolveTriggers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertAiBotAutoResolveTrigger = z.infer<typeof insertAiBotAutoResolveTriggerSchema>;
export type AiBotAutoResolveTrigger = typeof aiBotAutoResolveTriggers.$inferSelect;

export const aiBotAutoResolveExecutions = pgTable("ai_bot_auto_resolve_executions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  triggerId: varchar("trigger_id").notNull(),
  conversationId: varchar("conversation_id").notNull(),
  visitorMessageId: varchar("visitor_message_id").notNull(),
  claimedAt: timestamp("claimed_at").defaultNow(),
  executedAt: timestamp("executed_at"),
  success: boolean("success"),
  result: jsonb("result"),
}, (table) => [
  uniqueIndex("uq_ai_bot_auto_resolve_execution_cycle").on(
    table.tenantId,
    table.triggerId,
    table.conversationId,
    table.visitorMessageId,
  ),
  index("idx_ai_bot_auto_resolve_executions_tenant_cycle").on(
    table.tenantId,
    table.conversationId,
    table.visitorMessageId,
  ),
]);

export const insertAiBotAutoResolveExecutionSchema = createInsertSchema(aiBotAutoResolveExecutions).omit({
  id: true,
  claimedAt: true,
  executedAt: true,
});
export type InsertAiBotAutoResolveExecution = z.infer<typeof insertAiBotAutoResolveExecutionSchema>;
export type AiBotAutoResolveExecution = typeof aiBotAutoResolveExecutions.$inferSelect;

export const auditLogs = pgTable("audit_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  userId: varchar("user_id"),
  userName: text("user_name"),
  action: text("action").notNull().$type<"create" | "update" | "delete" | "duplicate">(),
  entityType: text("entity_type").notNull(),
  entityId: varchar("entity_id"),
  entityName: text("entity_name"),
  details: jsonb("details"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("audit_logs_tenant_idx").on(table.tenantId),
  index("audit_logs_created_at_idx").on(table.createdAt),
]);

export const insertAuditLogSchema = createInsertSchema(auditLogs).omit({
  id: true,
  createdAt: true,
});
export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;
export type AuditLog = typeof auditLogs.$inferSelect;

export const customDashboards = pgTable("custom_dashboards", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  isDefault: boolean("is_default").default(false),
  layoutConfig: jsonb("layout_config").default({}),
  globalFilters: jsonb("global_filters").default({}),
  createdBy: varchar("created_by"),
  updatedBy: varchar("updated_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("custom_dashboards_tenant_idx").on(table.tenantId),
]);

export const insertCustomDashboardSchema = createInsertSchema(customDashboards).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCustomDashboard = z.infer<typeof insertCustomDashboardSchema>;
export type CustomDashboard = typeof customDashboards.$inferSelect;

export const customDashboardWidgets = pgTable("custom_dashboard_widgets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  dashboardId: varchar("dashboard_id").notNull(),
  widgetType: text("widget_type").notNull(),
  title: text("title").notNull(),
  dataSource: text("data_source").notNull(),
  queryConfig: jsonb("query_config").default({}),
  layoutX: integer("layout_x").notNull().default(0),
  layoutY: integer("layout_y").notNull().default(0),
  layoutW: integer("layout_w").notNull().default(4),
  layoutH: integer("layout_h").notNull().default(3),
  refreshMode: text("refresh_mode").notNull().default("aggregate"),
  refreshSeconds: integer("refresh_seconds").default(30),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("custom_dashboard_widgets_dashboard_idx").on(table.dashboardId),
]);

export const insertCustomDashboardWidgetSchema = createInsertSchema(customDashboardWidgets).omit({
  id: true,
  createdAt: true,
});
export type InsertCustomDashboardWidget = z.infer<typeof insertCustomDashboardWidgetSchema>;
export type CustomDashboardWidget = typeof customDashboardWidgets.$inferSelect;

export const customDashboardAgents = pgTable("custom_dashboard_agents", {
  id: serial("id").primaryKey(),
  dashboardId: varchar("dashboard_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  userId: varchar("user_id").notNull(),
}, (table) => [
  uniqueIndex("custom_dashboard_agents_dashboard_user_idx").on(table.dashboardId, table.userId),
  index("custom_dashboard_agents_tenant_user_idx").on(table.tenantId, table.userId),
  index("custom_dashboard_agents_dashboard_idx").on(table.dashboardId),
]);

export const insertCustomDashboardAgentSchema = createInsertSchema(customDashboardAgents).omit({
  id: true,
});
export type InsertCustomDashboardAgent = z.infer<typeof insertCustomDashboardAgentSchema>;
export type CustomDashboardAgent = typeof customDashboardAgents.$inferSelect;

export const carrierVoiceRates = pgTable("carrier_voice_rates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  country: text("country").notNull(),
  iso2: text("iso2").notNull(),
  countryCode: text("country_code").notNull(),
  rate: numeric("rate", { precision: 12, scale: 6 }).notNull(),
  prefix: text("prefix").notNull(),
  billingInterval: integer("billing_interval").notNull().default(60),
}, (table) => [
  index("carrier_voice_rates_prefix_idx").on(table.prefix),
  index("carrier_voice_rates_iso2_idx").on(table.iso2),
  index("carrier_voice_rates_country_idx").on(table.country),
]);

export const insertCarrierVoiceRateSchema = createInsertSchema(carrierVoiceRates).omit({
  id: true,
});
export type InsertCarrierVoiceRate = z.infer<typeof insertCarrierVoiceRateSchema>;
export type CarrierVoiceRate = typeof carrierVoiceRates.$inferSelect;

export const carrierSmsRates = pgTable("carrier_sms_rates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  country: text("country").notNull(),
  countryIso: text("country_iso").notNull(),
  countryCode: text("country_code").notNull(),
  destinationNetwork: text("destination_network").notNull(),
  mcc: text("mcc"),
  mnc: text("mnc"),
  rate: numeric("rate", { precision: 12, scale: 6 }).notNull(),
}, (table) => [
  index("carrier_sms_rates_mcc_mnc_idx").on(table.mcc, table.mnc),
  index("carrier_sms_rates_country_iso_idx").on(table.countryIso),
  index("carrier_sms_rates_country_idx").on(table.country),
]);

export const insertCarrierSmsRateSchema = createInsertSchema(carrierSmsRates).omit({
  id: true,
});
export type InsertCarrierSmsRate = z.infer<typeof insertCarrierSmsRateSchema>;
export type CarrierSmsRate = typeof carrierSmsRates.$inferSelect;

export const tenantRateOverrides = pgTable("tenant_rate_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  rateType: text("rate_type").notNull(),
  country: text("country").notNull(),
  countryCode: text("country_code"),
  customRate: numeric("custom_rate", { precision: 12, scale: 6 }).notNull(),
  label: text("label"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("tenant_rate_overrides_tenant_idx").on(table.tenantId),
  index("tenant_rate_overrides_type_idx").on(table.tenantId, table.rateType),
]);

export const insertTenantRateOverrideSchema = createInsertSchema(tenantRateOverrides).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertTenantRateOverride = z.infer<typeof insertTenantRateOverrideSchema>;
export type TenantRateOverride = typeof tenantRateOverrides.$inferSelect;

export const carrierInboundVoiceRates = pgTable("carrier_inbound_voice_rates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  country: text("country").notNull(),
  iso2: text("iso2").notNull(),
  numberType: text("number_type").notNull(),
  rate: numeric("rate", { precision: 12, scale: 6 }).notNull(),
}, (table) => [
  index("carrier_inbound_voice_rates_iso2_idx").on(table.iso2),
  index("carrier_inbound_voice_rates_country_idx").on(table.country),
  index("carrier_inbound_voice_rates_type_idx").on(table.numberType),
]);

export const insertCarrierInboundVoiceRateSchema = createInsertSchema(carrierInboundVoiceRates).omit({
  id: true,
});
export type InsertCarrierInboundVoiceRate = z.infer<typeof insertCarrierInboundVoiceRateSchema>;
export type CarrierInboundVoiceRate = typeof carrierInboundVoiceRates.$inferSelect;

export const csatTriggerTypeEnum = pgEnum("csat_trigger_type", ["auto", "manual", "in_call"]);
export const csatSurveyStatusEnum = pgEnum("csat_survey_status", ["pending", "sent", "completed", "no_answer", "skipped"]);
export const csatQuestionTypeEnum = pgEnum("csat_question_type", ["score_1_5", "yes_no"]);

export const csatSurveyTemplates = pgTable("csat_survey_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  isActive: boolean("is_active").notNull().default(true),
  queueId: varchar("queue_id"),
  callerIdNumberId: varchar("caller_id_number_id"),
  questions: jsonb("questions").notNull().default(sql`'[]'::jsonb`),
  welcomeText: text("welcome_text").default("Hello! We'd like to ask you a few quick questions about your recent call. Your feedback helps us improve our service."),
  welcomeRecordingId: varchar("welcome_recording_id"),
  welcomeUseRecording: boolean("welcome_use_recording").notNull().default(false),
  thankYouText: text("thank_you_text").default("Thank you for your feedback. We appreciate your time. Goodbye."),
  thankYouRecordingId: varchar("thank_you_recording_id"),
  thankYouUseRecording: boolean("thank_you_use_recording").notNull().default(false),
  skipShortCalls: boolean("skip_short_calls").notNull().default(true),
  skipVoicemail: boolean("skip_voicemail").notNull().default(true),
  skipNoAnswer: boolean("skip_no_answer").notNull().default(true),
  skipTransferred: boolean("skip_transferred").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("csat_survey_templates_tenant_idx").on(table.tenantId),
]);

export const surveyQuestionSchema = z.object({
  text: z.string(),
  recordingId: z.string().nullable().optional(),
  useRecording: z.boolean().default(false),
  type: z.enum(["score_1_5", "yes_no"]),
});

export type SurveyQuestion = z.infer<typeof surveyQuestionSchema>;

export const insertCsatSurveyTemplateSchema = createInsertSchema(csatSurveyTemplates).omit({
  id: true,
  createdAt: true,
}).extend({
  questions: z.array(surveyQuestionSchema).min(1).max(5),
});
export type InsertCsatSurveyTemplate = z.infer<typeof insertCsatSurveyTemplateSchema>;
export type CsatSurveyTemplate = typeof csatSurveyTemplates.$inferSelect;

export const csatSurveys = pgTable("csat_surveys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  surveyTemplateId: varchar("survey_template_id"),
  callId: varchar("call_id"),
  contactId: varchar("contact_id"),
  agentId: varchar("agent_id"),
  queueId: varchar("queue_id"),
  customerNumber: text("customer_number").notNull(),
  triggerType: csatTriggerTypeEnum("trigger_type").notNull().default("auto"),
  surveyStatus: csatSurveyStatusEnum("survey_status").notNull().default("sent"),
  csatScore: integer("csat_score"),
  issueResolved: boolean("issue_resolved"),
  responses: jsonb("responses").default(sql`'[]'::jsonb`),
  surveyCallUuid: text("survey_call_uuid"),
  callDirection: text("call_direction"),
  surveyDuration: integer("survey_duration"),
  surveyCost: text("survey_cost"),
  surveySentAt: timestamp("survey_sent_at").defaultNow(),
  surveyCompletedAt: timestamp("survey_completed_at"),
}, (table) => [
  index("csat_surveys_tenant_idx").on(table.tenantId),
  index("csat_surveys_customer_number_idx").on(table.tenantId, table.customerNumber),
  index("csat_surveys_agent_idx").on(table.tenantId, table.agentId),
]);

export const insertCsatSurveySchema = createInsertSchema(csatSurveys).omit({
  id: true,
  surveySentAt: true,
  surveyCompletedAt: true,
});
export type InsertCsatSurvey = z.infer<typeof insertCsatSurveySchema>;
export type CsatSurvey = typeof csatSurveys.$inferSelect;

export const kbIngestJobs = pgTable("kb_ingest_jobs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  kbId: varchar("kb_id").notNull(),
  status: varchar("status").notNull().default("pending"),
  sourceType: varchar("source_type").notNull(),
  sourceRef: text("source_ref"),
  resultCount: integer("result_count"),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow(),
  completedAt: timestamp("completed_at"),
});
export type KbIngestJob = typeof kbIngestJobs.$inferSelect;

export const rolePermissions = pgTable("role_permissions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  permissionKey: text("permission_key").notNull(),
  role: text("role").notNull(),
  allowed: boolean("allowed").notNull().default(true),
});

export type RolePermission = typeof rolePermissions.$inferSelect;

export const callMonitorSessions = pgTable("call_monitor_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  callLogId: varchar("call_log_id").notNull(),
  supervisorUserId: varchar("supervisor_user_id").notNull(),
  conferenceName: text("conference_name").notNull(),
  conferenceCreated: boolean("conference_created").notNull().default(false),
  supervisorCallUuid: varchar("supervisor_call_uuid"),
  supervisorMemberId: varchar("supervisor_member_id"),
  customerCallUuid: varchar("customer_call_uuid"),
  mode: varchar("mode").notNull().default("listen"),
  modeHistory: jsonb("mode_history").default(sql`'[]'::jsonb`),
  startedAt: timestamp("started_at").defaultNow(),
  endedAt: timestamp("ended_at"),
});

export const insertCallMonitorSessionSchema = createInsertSchema(callMonitorSessions).omit({ id: true, startedAt: true, endedAt: true });
export type InsertCallMonitorSession = z.infer<typeof insertCallMonitorSessionSchema>;
export type CallMonitorSession = typeof callMonitorSessions.$inferSelect;

export const voiceAiAgents = pgTable("voice_ai_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  systemPrompt: text("system_prompt"),
  greetingMessage: text("greeting_message").default("Hello! How can I help you today?"),
  knowledgeBaseId: varchar("knowledge_base_id"),
  llmModel: text("llm_model").notNull().default("claude-sonnet-5"),
  deepgramVoice: text("deepgram_voice").default("aura-2-thalia-en"),
  ttsProvider: text("tts_provider").notNull().default("deepgram"),
  elevenLabsVoiceId: text("eleven_labs_voice_id"),
  elevenLabsApiKey: text("eleven_labs_api_key"),
  confidenceThreshold: numeric("confidence_threshold", { precision: 3, scale: 2 }).default("0.70"),
  language: text("language").default("en"),
  enabled: boolean("enabled").notNull().default(true),
  transferMode: text("transfer_mode").notNull().default("warm"),
  assignedQueueId: varchar("assigned_queue_id"),
  ivrKey: text("ivr_key"),
  callCount: integer("call_count").default(0),
  abTestEnabled: boolean("ab_test_enabled").default(false),
  abVariantBName: text("ab_variant_b_name"),
  abVariantBSystemPrompt: text("ab_variant_b_system_prompt"),
  abVariantBGreeting: text("ab_variant_b_greeting"),
  abVariantBWeight: integer("ab_variant_b_weight").default(50),
  afterHoursEnabled: boolean("after_hours_enabled").default(false),
  afterHoursAction: text("after_hours_action"),
  afterHoursForwardNumber: text("after_hours_forward_number"),
  afterHoursMessage: text("after_hours_message"),
  skillTags: text("skill_tags").array(),
  intentRoutingTable: jsonb("intent_routing_table").$type<Array<{intentKeywords: string[], targetAgentId: string, description?: string}>>().default([]),
  backgroundNoise: text("background_noise").notNull().default("none"),
  backgroundNoiseVolume: integer("background_noise_volume").default(50),
  bargeInSensitivity: text("barge_in_sensitivity").notNull().default("normal"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("voice_ai_agents_tenant_idx").on(table.tenantId),
]);

export const insertVoiceAiAgentSchema = createInsertSchema(voiceAiAgents).omit({
  id: true,
  callCount: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  llmModel: z.enum(["claude-sonnet-5", "claude-sonnet-4-5-20250929"]).optional(),
  confidenceThreshold: z.union([z.string(), z.number()]).transform(v => String(v)).optional(),
  abVariantBWeight: z.union([z.string(), z.number()]).transform(v => Number(v)).optional(),
});

export type InsertVoiceAiAgent = z.infer<typeof insertVoiceAiAgentSchema>;
export type VoiceAiAgent = typeof voiceAiAgents.$inferSelect;

export const voiceAiSessions = pgTable("voice_ai_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  agentId: varchar("agent_id").notNull(),
  callUuid: varchar("call_uuid"),
  callLogId: varchar("call_log_id"),
  callerNumber: text("caller_number"),
  transcript: jsonb("transcript").default([]),
  summary: text("summary"),
  durationSeconds: integer("duration_seconds"),
  costAmount: numeric("cost_amount", { precision: 12, scale: 4 }),
  escalated: boolean("escalated").default(false),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("voice_ai_sessions_tenant_idx").on(table.tenantId, table.createdAt),
]);

export type VoiceAiSession = typeof voiceAiSessions.$inferSelect;

export const voiceAiFlows = pgTable("voice_ai_flows", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  agentId: varchar("agent_id"),
  name: text("name").notNull(),
  description: text("description"),
  nodes: jsonb("nodes").default([]),
  edges: jsonb("edges").default([]),
  isPublished: boolean("is_published").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("voice_ai_flows_tenant_idx").on(table.tenantId),
]);

export const insertVoiceAiFlowSchema = createInsertSchema(voiceAiFlows).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertVoiceAiFlow = z.infer<typeof insertVoiceAiFlowSchema>;
export type VoiceAiFlow = typeof voiceAiFlows.$inferSelect;

export const voiceAiCallAnalytics = pgTable("voice_ai_call_analytics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  sessionId: varchar("session_id"),
  agentId: varchar("agent_id").notNull(),
  callLogId: varchar("call_log_id"),
  intentTags: text("intent_tags").array(),
  sentimentLabel: text("sentiment_label"),
  containmentOutcome: text("containment_outcome"),
  escalationReason: text("escalation_reason"),
  handlingTimeSeconds: integer("handling_time_seconds"),
  abVariant: text("ab_variant"),
  turnCount: integer("turn_count"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("voice_ai_call_analytics_tenant_idx").on(table.tenantId, table.createdAt),
]);
export type VoiceAiCallAnalytics = typeof voiceAiCallAnalytics.$inferSelect;

export const voiceAiFlowVersions = pgTable("voice_ai_flow_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  flowId: varchar("flow_id").notNull(),
  versionNumber: integer("version_number").notNull().default(1),
  nodes: jsonb("nodes").default([]),
  edges: jsonb("edges").default([]),
  publishedAt: timestamp("published_at"),
  publishedBy: varchar("published_by"),
  changeNote: text("change_note"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("voice_ai_flow_versions_flow_idx").on(table.flowId, table.versionNumber),
]);
export type VoiceAiFlowVersion = typeof voiceAiFlowVersions.$inferSelect;

// ── Email Campaigns ───────────────────────────────────────────────────────────

export const emailCampaignStatusEnum = pgEnum("email_campaign_status", [
  "draft", "scheduled", "in-progress", "paused", "completed", "cancelled",
]);

export const emailMessageStatusEnum = pgEnum("email_message_status", [
  "pending", "submitted", "delivered", "failed", "blocked", "bounced", "unsubscribed",
]);

export const emailCampaigns = pgTable("email_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  displayId: integer("display_id"),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  status: emailCampaignStatusEnum("status").notNull().default("draft"),
  senderId: varchar("sender_id"),
  templateId: varchar("template_id"),
  ongoing: boolean("ongoing").notNull().default(false),
  scheduledAt: timestamp("scheduled_at"),
  currentCycleStartedAt: timestamp("current_cycle_started_at"),
  intervalDays: integer("interval_days").default(0),
  intervalHours: integer("interval_hours").default(0),
  hourlyLimit: integer("hourly_limit").default(0),
  overrideBusinessHours: boolean("override_business_hours").notNull().default(false),
  autoBounceToDnc: boolean("auto_bounce_to_dnc").notNull().default(false),
  fromName: text("from_name"),
  submittedCount: integer("submitted_count").notNull().default(0),
  deliveredCount: integer("delivered_count").notNull().default(0),
  failedCount: integer("failed_count").notNull().default(0),
  blockedCount: integer("blocked_count").notNull().default(0),
  bouncedCount: integer("bounced_count").notNull().default(0),
  openedCount: integer("opened_count").notNull().default(0),
  clickedCount: integer("clicked_count").notNull().default(0),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("email_campaigns_tenant_idx").on(table.tenantId, table.createdAt),
]);

export const insertEmailCampaignSchema = createInsertSchema(emailCampaigns).omit({
  id: true,
  submittedCount: true,
  deliveredCount: true,
  failedCount: true,
  blockedCount: true,
  bouncedCount: true,
  openedCount: true,
  clickedCount: true,
  currentCycleStartedAt: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});
export type InsertEmailCampaign = z.infer<typeof insertEmailCampaignSchema>;
export type EmailCampaign = typeof emailCampaigns.$inferSelect;

export const emailCampaignLists = pgTable("email_campaign_lists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull(),
  contactListId: varchar("contact_list_id").notNull(),
  isBlockList: boolean("is_block_list").notNull().default(false),
}, (table) => [
  uniqueIndex("email_campaign_lists_unique_idx").on(table.campaignId, table.contactListId, table.isBlockList),
]);

export const insertEmailCampaignListSchema = createInsertSchema(emailCampaignLists).omit({ id: true });
export type InsertEmailCampaignList = z.infer<typeof insertEmailCampaignListSchema>;
export type EmailCampaignList = typeof emailCampaignLists.$inferSelect;

export const emailCampaignMessages = pgTable("email_campaign_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  campaignId: varchar("campaign_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  email: text("email").notNull(),
  status: emailMessageStatusEnum("status").notNull().default("pending"),
  cycleStartedAt: timestamp("cycle_started_at"),
  elasticMessageId: text("elastic_message_id"),
  sentAt: timestamp("sent_at"),
  deliveredAt: timestamp("delivered_at"),
  bouncedAt: timestamp("bounced_at"),
  openedAt: timestamp("opened_at"),
  clickedAt: timestamp("clicked_at"),
  errorMessage: text("error_message"),
  claimedAt: timestamp("claimed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("email_campaign_messages_unique_idx").on(table.campaignId, table.contactId, table.cycleStartedAt),
  index("email_campaign_messages_tenant_idx").on(table.tenantId, table.campaignId),
]);

export const insertEmailCampaignMessageSchema = createInsertSchema(emailCampaignMessages).omit({
  id: true,
  elasticMessageId: true,
  sentAt: true,
  deliveredAt: true,
  bouncedAt: true,
  openedAt: true,
  clickedAt: true,
  errorMessage: true,
  createdAt: true,
});
export type InsertEmailCampaignMessage = z.infer<typeof insertEmailCampaignMessageSchema>;
export type EmailCampaignMessage = typeof emailCampaignMessages.$inferSelect;

export const emailSenderSignatures = pgTable("email_sender_signatures", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  email: text("email").notNull(),
  fromName: text("from_name"),
  elasticId: text("elastic_id"),
  verifiedAt: timestamp("verified_at"),
  verificationToken: text("verification_token"),
  verificationTokenExpiry: timestamp("verification_token_expiry"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("email_sender_signatures_unique_idx").on(table.tenantId, table.email),
]);

export const insertEmailSenderSignatureSchema = createInsertSchema(emailSenderSignatures).omit({
  id: true,
  verifiedAt: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertEmailSenderSignature = z.infer<typeof insertEmailSenderSignatureSchema>;
export type EmailSenderSignature = typeof emailSenderSignatures.$inferSelect;

export const emailTemplates = pgTable("email_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  title: text("title").notNull(),
  subject: text("subject").notNull(),
  cc: text("cc"),
  bcc: text("bcc"),
  htmlBody: text("html_body"),
  designJson: jsonb("design_json"),
  elasticTemplateId: text("elastic_template_id"),
  stripoTemplateId: text("stripo_template_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("email_templates_tenant_idx").on(table.tenantId, table.createdAt),
]);

export const insertEmailTemplateSchema = createInsertSchema(emailTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});
export type InsertEmailTemplate = z.infer<typeof insertEmailTemplateSchema>;
export type EmailTemplate = typeof emailTemplates.$inferSelect;

// ─────────────────────────────────────────────────────────────────────────────

export const voicemailStatuses = pgTable("voicemail_statuses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  color: text("color").notNull().default("#6b7280"),
  isFinal: boolean("is_final").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").default(0),
  parentId: varchar("parent_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertVoicemailStatusSchema = createInsertSchema(voicemailStatuses).omit({
  id: true,
  createdAt: true,
});
export type InsertVoicemailStatus = z.infer<typeof insertVoicemailStatusSchema>;
export type VoicemailStatus = typeof voicemailStatuses.$inferSelect;

// ─────────────────────────────────────────────────────────────────────────────
// Dedicated voicemails table. This table already exists in the database (created
// out-of-band, before it was tracked here) with a subset of these columns; the
// rest are added by migration 0035. Voicemail data is being migrated off
// call_logs.vm_* / call_logs.is_voicemail onto this table — see call_logs
// comment near isVoicemail for the deprecation note.
export const voicemails = pgTable("voicemails", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  callLogId: varchar("call_log_id"),
  fromNumber: text("from_number").notNull(),
  toNumber: text("to_number").notNull(),
  recordingUrl: text("recording_url"),
  duration: integer("duration").default(0),
  isRead: boolean("is_read").default(false),
  transcription: text("transcription"),
  phoneNumberId: varchar("phone_number_id"),
  queueId: varchar("queue_id"),
  displayId: text("display_id"),
  assignedTo: varchar("assigned_to"),
  ivrMenuId: varchar("ivr_menu_id"),
  contactId: varchar("contact_id"),
  contactName: text("contact_name"),
  agentId: varchar("agent_id"),
  agentEndpointId: varchar("agent_endpoint_id"),
  agentName: text("agent_name"),
  direction: text("direction"),
  startedAt: timestamp("started_at"),
  vmStatus: text("vm_status"),
  vmIsNew: boolean("vm_is_new").default(true),
  vmNotes: text("vm_notes"),
  vmArchived: boolean("vm_archived").default(false),
  vmHandledBy: text("vm_handled_by"),
  activityImportLogId: varchar("activity_import_log_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_voicemails_tenant_id").on(table.tenantId),
  index("idx_voicemails_tenant_started").on(table.tenantId, table.startedAt),
  uniqueIndex("uq_voicemails_call_log_id").on(table.callLogId).where(sql`${table.callLogId} IS NOT NULL`),
  index("idx_voicemails_queue_id").on(table.queueId),
  index("idx_voicemails_ivr_menu_id").on(table.ivrMenuId),
  index("idx_voicemails_phone_number_id").on(table.phoneNumberId),
  index("idx_voicemails_contact_id").on(table.contactId),
]);

export const insertVoicemailSchema = createInsertSchema(voicemails).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertVoicemail = z.infer<typeof insertVoicemailSchema>;
export type Voicemail = typeof voicemails.$inferSelect;

// ─────────────────────────────────────────────────────────────────────────────

export const appointmentEventTypeEnum = pgEnum("appointment_event_type", ["booked", "showed", "no_show", "rescheduled", "cancelled"]);

export const appointmentEvents = pgTable("appointment_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  contactId: varchar("contact_id"),
  eventType: appointmentEventTypeEnum("event_type"),
  appointmentDate: timestamp("appointment_date"),
  clinicName: text("clinic_name"),
  patientPhone: text("patient_phone").notNull(),
  externalId: text("external_id"),
  rawPayload: jsonb("raw_payload"),
  receivedAt: timestamp("received_at").defaultNow(),
}, (table) => [
  index("idx_appointment_events_tenant_id").on(table.tenantId),
  index("idx_appointment_events_contact_id").on(table.contactId),
]);

export const insertAppointmentEventSchema = createInsertSchema(appointmentEvents).omit({
  id: true,
  receivedAt: true,
});
export type InsertAppointmentEvent = z.infer<typeof insertAppointmentEventSchema>;
export type AppointmentEvent = typeof appointmentEvents.$inferSelect;

// ─────────────────────────────────────────────────────────────────────────────

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
});

export type LoginInput = z.infer<typeof loginSchema>;


// ─────────────────────────────────────────────────────────────────────────────
// Platform White Label

export const platformWhiteLabel = pgTable("platform_white_label", {
  id: integer("id").primaryKey().default(1),
  appName: text("app_name").notNull().default("CallKarma.ai"),
  lightLogoUrl: text("light_logo_url"),
  darkLogoUrl: text("dark_logo_url"),
  faviconUrl: text("favicon_url"),
  loginTagline: text("login_tagline").notNull().default("Contact Center Platform"),
  loginHeroSubtitle: text("login_hero_subtitle").notNull().default("Manage calls, queues, and agents all in one place"),
  emailSenderName: text("email_sender_name").notNull().default("CallKarma.ai"),
  emailSenderAddress: text("email_sender_address").notNull().default("alerts@callkarma.ai"),
  hideSidebarAppName: boolean("hide_sidebar_app_name").default(false),
  hideSidebarTagline: boolean("hide_sidebar_tagline").default(false),
  hideLoginAppName: boolean("hide_login_app_name").default(false),
  hideLoginTagline: boolean("hide_login_tagline").default(false),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertPlatformWhiteLabelSchema = createInsertSchema(platformWhiteLabel).omit({
  id: true,
  updatedAt: true,
});
export type InsertPlatformWhiteLabel = z.infer<typeof insertPlatformWhiteLabelSchema>;
export type PlatformWhiteLabel = typeof platformWhiteLabel.$inferSelect;

// ─────────────────────────────────────────────────────────────────────────────
// Platform AI Settings

export const platformAiSettings = pgTable("platform_ai_settings", {
  id: integer("id").primaryKey().default(1),
  defaultModel: text("default_model").notNull().default("claude-sonnet-4-5-20250929"),
  updatedAt: timestamp("updated_at").defaultNow(),
  /** Super-admin emergency instruction injected into every AI Bot request as a live, non-cached directive. */
  emergencyInstruction: text("emergency_instruction"),
});

export const insertPlatformAiSettingsSchema = createInsertSchema(platformAiSettings).omit({
  id: true,
  updatedAt: true,
});
export type InsertPlatformAiSettings = z.infer<typeof insertPlatformAiSettingsSchema>;
export type PlatformAiSettings = typeof platformAiSettings.$inferSelect;

// ─────────────────────────────────────────────────────────────────────────────
// AI Analysis Integration

export const aiCallAnalyses = pgTable("ai_call_analyses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  callLogId: varchar("call_log_id"),
  callUuid: text("call_uuid"),
  agentId: varchar("agent_id"),
  agentName: text("agent_name"),
  durationSeconds: integer("duration_seconds"),
  callDirection: text("call_direction"),
  callerNumber: text("caller_number"),
  calledNumber: text("called_number"),
  overallScore: numeric("overall_score"),
  sentimentScore: numeric("sentiment_score"),
  agentSentimentScore: numeric("agent_sentiment_score"),
  empathyScore: numeric("empathy_score"),
  communicationScore: numeric("communication_score"),
  resolutionScore: numeric("resolution_score"),
  complianceScore: numeric("compliance_score"),
  transcript: text("transcript"),
  transcriptUrl: text("transcript_url"),
  summary: text("summary"),
  flags: jsonb("flags").$type<Array<{ type: string; severity: string; description: string }>>(),
  topics: jsonb("topics").$type<string[]>(),
  keywords: jsonb("keywords").$type<string[]>(),
  rawPayload: jsonb("raw_payload"),
  analyzedAt: timestamp("analyzed_at"),
  receivedAt: timestamp("received_at").defaultNow(),
}, (table) => [
  index("idx_ai_call_analyses_tenant_id").on(table.tenantId),
  index("idx_ai_call_analyses_call_uuid").on(table.callUuid),
  index("idx_ai_call_analyses_agent_id").on(table.agentId),
  index("idx_ai_call_analyses_received_at").on(table.tenantId, table.receivedAt),
]);

export const insertAiCallAnalysisSchema = createInsertSchema(aiCallAnalyses).omit({
  id: true,
  receivedAt: true,
});
export type InsertAiCallAnalysis = z.infer<typeof insertAiCallAnalysisSchema>;
export type AiCallAnalysis = typeof aiCallAnalyses.$inferSelect;

export const aiCustomScores = pgTable("ai_custom_scores", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  analysisId: varchar("analysis_id").notNull(),
  category: text("category").notNull(),
  score: numeric("score"),
  notes: text("notes"),
  // QA Scorecard fields
  scenarioName: text("scenario_name"),
  maxPoints: numeric("max_points"),
  pointsDeducted: numeric("points_deducted"),
  answers: jsonb("answers").$type<Array<{ question: string; answer: "yes" | "no" | "na"; deduction: number; deductionReason?: string }>>(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_ai_custom_scores_analysis_id").on(table.analysisId),
]);

export const insertAiCustomScoreSchema = createInsertSchema(aiCustomScores).omit({
  id: true,
  createdAt: true,
});
export type InsertAiCustomScore = z.infer<typeof insertAiCustomScoreSchema>;
export type AiCustomScore = typeof aiCustomScores.$inferSelect;

export const tenantAiAnalysisSettings = pgTable("tenant_ai_analysis_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().unique(),
  enabled: boolean("enabled").notNull().default(false),
  webhookSecret: text("webhook_secret"),
  hmacEnabled: boolean("hmac_enabled").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTenantAiAnalysisSettingsSchema = createInsertSchema(tenantAiAnalysisSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertTenantAiAnalysisSettings = z.infer<typeof insertTenantAiAnalysisSettingsSchema>;
export type TenantAiAnalysisSettings = typeof tenantAiAnalysisSettings.$inferSelect;

export const tenantCustomCallerIds = pgTable("tenant_custom_caller_ids", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  number: text("number").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_tenant_custom_caller_ids_tenant_id").on(table.tenantId),
]);

export const insertTenantCustomCallerIdSchema = createInsertSchema(tenantCustomCallerIds).omit({
  id: true,
  createdAt: true,
});
export type InsertTenantCustomCallerId = z.infer<typeof insertTenantCustomCallerIdSchema>;
export type TenantCustomCallerId = typeof tenantCustomCallerIds.$inferSelect;

export const callLogRecordings = pgTable("call_log_recordings", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id").notNull(),
  callLogId: varchar("call_log_id").notNull().references(() => callLogs.id, { onDelete: "cascade" }),
  callUuid: text("call_uuid"),
  provider: text("provider").notNull().default("plivo"),
  providerRecordingId: text("provider_recording_id"),
  providerCallUuid: text("provider_call_uuid"),
  sourceUrl: text("source_url").notNull(),
  s3Key: text("s3_key"),
  segmentType: text("segment_type").notNull().default("call"),
  segmentIndex: integer("segment_index").notNull().default(0),
  duration: integer("duration"),
  startedAt: timestamp("started_at"),
  /** Authoritative end timestamp from the provider payload — used for COALESCE-ordered stitching. */
  endedAt: timestamp("ended_at"),
  movedToS3: boolean("moved_to_s3").notNull().default(false),
  storageStatus: text("storage_status").notNull().default("provider"),
  byteSize: integer("byte_size"),
  sha256: text("sha256"),
  mimeType: text("mime_type"),
  verifiedAt: timestamp("verified_at", { withTimezone: true }),
  isFinal: boolean("is_final").notNull().default(false),
  compositionGeneration: integer("composition_generation").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("call_log_recordings_log_segment_idx").on(table.callLogId, table.segmentIndex),
  index("idx_call_log_recordings_tenant_id").on(table.tenantId),
  index("idx_call_log_recordings_call_uuid").on(table.callUuid),
]);

export const callRecordingManifests = pgTable("call_recording_manifests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  callLogId: varchar("call_log_id").notNull().references(() => callLogs.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("not_expected"),
  expected: boolean("expected").notNull().default(false),
  policySnapshot: jsonb("policy_snapshot").$type<RecordingPolicySnapshot>().notNull(),
  paused: boolean("paused").notNull().default(false),
  pauseStartedAt: timestamp("pause_started_at", { withTimezone: true }),
  pauseIntervals: jsonb("pause_intervals").$type<RecordingPauseInterval[]>().notNull().default([]),
  captureHandle: jsonb("capture_handle").$type<RecordingCaptureHandle | null>(),
  finalAssetId: integer("final_asset_id").references(() => callLogRecordings.id, { onDelete: "set null" }),
  rawSegmentCount: integer("raw_segment_count").notNull().default(0),
  compositionGeneration: integer("composition_generation").notNull().default(0),
  failureCode: text("failure_code"),
  failureDetail: text("failure_detail"),
  lastReconciledAt: timestamp("last_reconciled_at", { withTimezone: true }),
  reconciliationExhaustedAt: timestamp("reconciliation_exhausted_at", { withTimezone: true }),
  callEndedEventSentAt: timestamp("call_ended_event_sent_at", { withTimezone: true }),
  recordingReadyEventSentAt: timestamp("recording_ready_event_sent_at", { withTimezone: true }),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  uniqueIndex("call_recording_manifests_call_log_unique").on(table.callLogId),
  index("idx_call_recording_manifests_tenant").on(table.tenantId),
  index("idx_call_recording_manifests_tenant_status").on(table.tenantId, table.status),
  index("idx_call_recording_manifests_final_asset").on(table.finalAssetId),
]);

export const recordingProviderEvents = pgTable("recording_provider_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  provider: text("provider").notNull(),
  providerAccountId: text("provider_account_id").notNull(),
  eventKey: text("event_key").notNull(),
  eventType: text("event_type").notNull(),
  tenantId: varchar("tenant_id"),
  callLogId: varchar("call_log_id").references(() => callLogs.id, { onDelete: "set null" }),
  providerCallUuid: text("provider_call_uuid"),
  providerRequestUuid: text("provider_request_uuid"),
  conferenceRoom: text("conference_room"),
  providerRecordingId: text("provider_recording_id"),
  segmentKind: text("segment_kind").notNull().default("conversation"),
  payload: jsonb("payload").$type<Record<string, unknown>>().notNull(),
  status: text("status").notNull().default("unmatched"),
  attempts: integer("attempts").notNull().default(0),
  dueAt: timestamp("due_at", { withTimezone: true }).notNull().defaultNow(),
  lastError: text("last_error"),
  processedAt: timestamp("processed_at", { withTimezone: true }),
  receivedAt: timestamp("received_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  uniqueIndex("recording_provider_events_event_key_unique").on(table.eventKey),
  index("idx_recording_provider_events_tenant").on(table.tenantId),
  index("idx_recording_provider_events_call_log").on(table.callLogId),
  index("idx_recording_provider_events_status_due").on(table.status, table.dueAt),
  index("idx_recording_provider_events_recording").on(table.provider, table.providerRecordingId),
]);

export const recordingCorrelations = pgTable("recording_correlations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  provider: text("provider").notNull(),
  providerAccountId: text("provider_account_id").notNull(),
  tenantId: varchar("tenant_id").notNull(),
  callLogId: varchar("call_log_id").notNull().references(() => callLogs.id, { onDelete: "cascade" }),
  correlationType: text("correlation_type").notNull(),
  externalId: text("external_id").notNull(),
  segmentKind: text("segment_kind").notNull().default("conversation"),
  active: boolean("active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  uniqueIndex("recording_correlations_provider_external_unique")
    .on(table.provider, table.providerAccountId, table.correlationType, table.externalId),
  index("idx_recording_correlations_tenant").on(table.tenantId),
  index("idx_recording_correlations_call_log").on(table.callLogId),
  index("idx_recording_correlations_lookup").on(table.provider, table.correlationType, table.externalId),
]);

export const recordingJobs = pgTable("recording_jobs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull(),
  callLogId: varchar("call_log_id").notNull().references(() => callLogs.id, { onDelete: "cascade" }),
  manifestId: varchar("manifest_id").notNull().references(() => callRecordingManifests.id, { onDelete: "cascade" }),
  jobType: text("job_type").notNull(),
  status: text("status").notNull().default("pending"),
  idempotencyKey: text("idempotency_key").notNull(),
  payload: jsonb("payload").$type<Record<string, unknown>>().notNull().default({}),
  attempts: integer("attempts").notNull().default(0),
  maxAttempts: integer("max_attempts").notNull().default(8),
  dueAt: timestamp("due_at", { withTimezone: true }).notNull().defaultNow(),
  lockedAt: timestamp("locked_at", { withTimezone: true }),
  leaseExpiresAt: timestamp("lease_expires_at", { withTimezone: true }),
  lockToken: text("lock_token"),
  lastErrorCode: text("last_error_code"),
  lastError: text("last_error"),
  completedAt: timestamp("completed_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  uniqueIndex("recording_jobs_idempotency_unique").on(table.idempotencyKey),
  index("idx_recording_jobs_tenant").on(table.tenantId),
  index("idx_recording_jobs_call_log").on(table.callLogId),
  index("idx_recording_jobs_manifest").on(table.manifestId),
  index("idx_recording_jobs_status_due").on(table.status, table.dueAt),
  index("idx_recording_jobs_status_lease").on(table.status, table.leaseExpiresAt),
]);

export type CallRecordingManifest = typeof callRecordingManifests.$inferSelect;
export type RecordingProviderEvent = typeof recordingProviderEvents.$inferSelect;
export type RecordingCorrelation = typeof recordingCorrelations.$inferSelect;
export type RecordingJob = typeof recordingJobs.$inferSelect;

// ─── Recording Stitch Jobs ────────────────────────────────────────────────────

/**
 * Tracks background FFmpeg stitch jobs for multi-segment (stop/resume) recordings.
 * One row per call_log_id (UNIQUE); status cycles: pending → running → done/failed.
 */
export const recordingStitchJobs = pgTable("recording_stitch_jobs", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id").notNull(),
  callLogId: varchar("call_log_id").notNull().references(() => callLogs.id, { onDelete: "cascade" }),
  /** pending | running | done | failed */
  status: text("status").notNull().default("pending"),
  /** Number of raw segments present when the stitch was last scheduled. */
  rawSegmentCount: integer("raw_segment_count").notNull().default(0),
  /** S3 key of the combined file once status = 'done'. */
  resultS3Key: text("result_s3_key"),
  /** Human-readable error message when status = 'failed'. */
  error: text("error"),
  /** Total number of stitch attempts made so far. */
  attempts: integer("attempts").notNull().default(0),
  // ── Hardening columns — only active when RECORDING_HARDENING_ENABLED=true ──────
  /** Earliest time this job may next be claimed (exponential backoff). */
  nextAttemptAt: timestamp("next_attempt_at", { withTimezone: true }),
  /** Wall-clock time when the current worker acquired the lease. */
  lockedAt: timestamp("locked_at", { withTimezone: true }),
  /** Lease expiry; another worker may reclaim after this timestamp. */
  leaseExpiresAt: timestamp("lease_expires_at", { withTimezone: true }),
  /** UUID issued to the worker that currently holds the lease. */
  lockToken: text("lock_token"),
  /** True after max-attempts exhausted; never retried automatically. */
  permanentlyFailed: boolean("permanently_failed").notNull().default(false),
  /** Snapshot of call_logs.recording_source_generation taken at claim time. */
  sourceGeneration: integer("source_generation").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("recording_stitch_jobs_call_log_idx").on(table.callLogId),
  index("idx_recording_stitch_jobs_tenant_id").on(table.tenantId),
  index("idx_recording_stitch_jobs_status").on(table.status),
]);

export type RecordingStitchJob = typeof recordingStitchJobs.$inferSelect;

// ─── Recording Hardening tables (Task #570) ───────────────────────────────────

/**
 * DB-backed S3 archival retry queue — replaces the in-memory queue in
 * recording-archiver.ts when RECORDING_HARDENING_ENABLED=true.
 * One row per call_log_recordings segment; UNIQUE on segment_id prevents
 * duplicate archive jobs.
 */
export const recordingArchiveJobs = pgTable("recording_archive_jobs", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id").notNull(),
  callLogId: varchar("call_log_id").notNull().references(() => callLogs.id, { onDelete: "cascade" }),
  segmentId: integer("segment_id").unique().references(() => callLogRecordings.id, { onDelete: "cascade" }),
  s3Key: text("s3_key"),
  status: text("status").notNull().default("pending"),
  attempts: integer("attempts").notNull().default(0),
  nextAttemptAt: timestamp("next_attempt_at"),
  lockedAt: timestamp("locked_at"),
  leaseExpiresAt: timestamp("lease_expires_at"),
  lockToken: text("lock_token"),
  error: text("error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_recording_archive_jobs_tenant_id").on(table.tenantId),
  index("idx_recording_archive_jobs_call_log_id").on(table.callLogId),
  index("idx_recording_archive_jobs_status_next_attempt").on(table.status, table.nextAttemptAt),
]);
export type RecordingArchiveJob = typeof recordingArchiveJobs.$inferSelect;

/**
 * Durable mapping: tenantId + conferenceRoom + transferId → callLogId.
 * Populated when a warm-transfer leg starts recording so that the recording
 * callback can correlate without guessing.
 * UNIQUE on (tenant_id, conference_room, transfer_id).
 */
export const warmTransferRecordingCorrelation = pgTable("warm_transfer_recording_correlation", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id").notNull(),
  conferenceRoom: text("conference_room").notNull(),
  transferId: text("transfer_id").notNull(),
  callLogId: varchar("call_log_id").notNull().references(() => callLogs.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("idx_warm_transfer_rec_corr_unique").on(table.tenantId, table.conferenceRoom, table.transferId),
  index("idx_warm_transfer_rec_corr_tenant").on(table.tenantId),
  index("idx_warm_transfer_rec_corr_call_log").on(table.callLogId),
]);
export type WarmTransferRecordingCorrelation = typeof warmTransferRecordingCorrelation.$inferSelect;

/**
 * Tenant-scoped durable store for failed or cross-tenant recording callbacks.
 * Callbacks that cannot be safely associated with a call are stored here
 * (not merely logged) for later manual inspection.
 * Bounded retention via expires_at — 30-day default.
 */
export const recordingCallbackQuarantine = pgTable("recording_callback_quarantine", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id").notNull(),
  rawPayload: jsonb("raw_payload").notNull(),
  reason: text("reason").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  expiresAt: timestamp("expires_at"),
}, (table) => [
  index("idx_recording_callback_quarantine_tenant").on(table.tenantId),
  index("idx_recording_callback_quarantine_expires").on(table.expiresAt),
]);
export type RecordingCallbackQuarantine = typeof recordingCallbackQuarantine.$inferSelect;

export const insertCallLogRecordingSchema = createInsertSchema(callLogRecordings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCallLogRecording = z.infer<typeof insertCallLogRecordingSchema>;
export type CallLogRecording = typeof callLogRecordings.$inferSelect;

// ─── SIP Resilience tables (Task #440) ────────────────────────────────────────

/**
 * One-to-many agent-leg mapping: one call_log can have multiple agent legs
 * (ring-all). Each row tracks a single Plivo call leg dialled to an agent device.
 */
export const callProviderLegs = pgTable("call_provider_legs", {
  id: text("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull(),
  callLogId: text("call_log_id").notNull().references(() => callLogs.id, { onDelete: "cascade" }),
  agentEndpointId: text("agent_endpoint_id"),
  deviceEndpointId: text("device_endpoint_id"),
  /** RequestUUID returned by calls.create() — used for early-leg lookup before the answer webhook. */
  providerRequestUuid: text("provider_request_uuid"),
  /** Actual CallUUID from the answer webhook. */
  providerCallUuid: text("provider_call_uuid"),
  /** ringing | answered | completed | cancelled */
  status: text("status").notNull().default("ringing"),
  /** true for the leg that answered and became the winner. At most one per call_log. */
  isWinningLeg: boolean("is_winning_leg").notNull().default(false),
  endedAt: timestamp("ended_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  index("idx_call_provider_legs_log").on(table.callLogId, table.createdAt),
]);

export type CallProviderLeg = typeof callProviderLegs.$inferSelect;

/**
 * Per-endpoint persistent SIP health state.
 * endpoint_id is the primary key; one row per Plivo SIP endpoint.
 */
export const sipHealth = pgTable("sip_health", {
  endpointId: text("endpoint_id").primaryKey(),
  tenantId: text("tenant_id").notNull(),
  unhealthyUntil: timestamp("unhealthy_until", { withTimezone: true }),
  healthEpisodeId: text("health_episode_id"),
  failureReason: text("failure_reason"),
  sipUri: text("sip_uri"),
  consecutiveFailures: integer("consecutive_failures").notNull().default(0),
  lastFailureAt: timestamp("last_failure_at", { withTimezone: true }),
  lastSuccessAt: timestamp("last_success_at", { withTimezone: true }),
}, (table) => [
  index("idx_sip_health_tenant_unhealthy").on(table.tenantId, table.unhealthyUntil),
  index("idx_sip_health_tenant_endpoint_episode").on(table.tenantId, table.endpointId, table.healthEpisodeId),
]);

export type SipHealth = typeof sipHealth.$inferSelect;

/**
 * Account-wide 429 / rate-limit cooldown per provider account.
 * Multiple tenants may share the same Plivo auth ID, so this is keyed on
 * (provider, provider_account_id) rather than tenant_id.
 */
export const providerAccountHealth = pgTable("provider_account_health", {
  provider: text("provider").notNull(),
  providerAccountId: text("provider_account_id").notNull(),
  rateLimitBackoffUntil: timestamp("rate_limit_backoff_until", { withTimezone: true }),
  /** UUID retained across repeated 429s within the same cooldown window. */
  cooldownEpisodeId: text("cooldown_episode_id"),
  reason: text("reason"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  primaryKey({ columns: [table.provider, table.providerAccountId] }),
]);

export type ProviderAccountHealth = typeof providerAccountHealth.$inferSelect;

/**
 * Tenant-scoped configuration-error suppression per provider account.
 * 401/403 errors are per-tenant (wrong auth for that tenant) rather than
 * account-wide, so they live here instead of provider_account_health.
 */
export const providerTenantHealth = pgTable("provider_tenant_health", {
  provider: text("provider").notNull(),
  providerAccountId: text("provider_account_id").notNull(),
  tenantId: text("tenant_id").notNull(),
  configSuppressedUntil: timestamp("config_suppressed_until", { withTimezone: true }),
  cooldownEpisodeId: text("cooldown_episode_id"),
  reason: text("reason"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  primaryKey({ columns: [table.provider, table.providerAccountId, table.tenantId] }),
]);

export type ProviderTenantHealth = typeof providerTenantHealth.$inferSelect;

/**
 * Routing failure log — one row per agent-device dial attempt that failed
 * before or during ringing. Used to exclude unhealthy devices across polling
 * cycles for the same call UUID.
 */
export const queueAssignmentAttempts = pgTable("queue_assignment_attempts", {
  id: text("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull(),
  callUuid: text("call_uuid"),
  callLogId: text("call_log_id"),
  endpointId: text("endpoint_id"),
  deviceEndpointId: text("device_endpoint_id"),
  sipUri: text("sip_uri"),
  attemptedAt: timestamp("attempted_at", { withTimezone: true }).notNull().defaultNow(),
  failureReason: text("failure_reason"),
  httpStatus: integer("http_status"),
  providerErrorCode: text("provider_error_code"),
  providerRequestId: text("provider_request_id"),
  hangupCause: text("hangup_cause"),
  hangupCode: integer("hangup_code"),
  appInstanceId: text("app_instance_id"),
  rawErrorMessage: text("raw_error_message"),
  healthEpisodeId: text("health_episode_id"),
  cooldownEpisodeId: text("cooldown_episode_id"),
}, (table) => [
  index("idx_qaa_tenant_attempted").on(table.tenantId, table.attemptedAt),
  index("idx_qaa_call_uuid").on(table.callUuid, table.attemptedAt),
  index("idx_qaa_device").on(table.deviceEndpointId, table.attemptedAt),
  index("idx_qaa_tenant_call_uuid").on(table.tenantId, table.callUuid, table.attemptedAt),
  index("idx_qaa_device_episode").on(table.tenantId, table.deviceEndpointId, table.healthEpisodeId),
]);

export type QueueAssignmentAttempt = typeof queueAssignmentAttempts.$inferSelect;

/**
 * Warm-transfer leg-pairing map: aLegUuid → bLegUuid.
 * Persisted so it survives server restarts and process failover.
 */
export const providerLegMap = pgTable("provider_leg_map", {
  aLegUuid: text("a_leg_uuid").primaryKey(),
  bLegUuid: text("b_leg_uuid").notNull(),
  callLogId: text("call_log_id"),
  endpointId: text("endpoint_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  index("idx_plm_created_at").on(table.createdAt),
]);

export type ProviderLegMap = typeof providerLegMap.$inferSelect;

/**
 * Durable active-hold store. Records the exact customer UUID that hold music
 * was started on so that Unhold can stop music on the correct leg even after a
 * server restart or across multiple application replicas (autoscale deployment).
 *
 * provider_leg_map records leg topology at dial time; it does NOT track whether
 * a hold episode is currently active.  This table fills that gap.
 *
 * Key: (tenant_id, call_log_id) — one active hold per call per tenant.
 */
export const activeHolds = pgTable("active_holds", {
  tenantId: text("tenant_id").notNull(),
  callLogId: text("call_log_id").notNull(),
  customerUuid: text("customer_uuid").notNull(),
  heldAt: timestamp("held_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  primaryKey({ columns: [table.tenantId, table.callLogId] }),
  index("idx_active_holds_held_at").on(table.heldAt),
]);

export type ActiveHold = typeof activeHolds.$inferSelect;

/**
 * Durable dedup store for Plivo dial-callback webhooks.
 * Unique partial index on payload_hash (SHA-256 of the complete sorted body) ensures
 * only byte-for-byte identical Plivo retry deliveries are suppressed. Distinct events
 * that share the same legs/status but differ in hangupCause or any other field produce
 * a different hash and are processed normally.
 * 30-day retention via idx_plivo_callback_events_created_at.
 */
export const plivoCallbackEvents = pgTable("plivo_callback_events", {
  id: text("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id"),
  callUuid: text("call_uuid").notNull(),
  dialCallUuid: text("dial_call_uuid").notNull(),
  dialStatus: text("dial_status").notNull().default("unknown"),
  /** SHA-256 hex of the full sorted JSON body. The dedup unique index is on this. */
  payloadHash: text("payload_hash"),
  rawPayload: jsonb("raw_payload"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  index("idx_plivo_callback_events_created_at").on(table.createdAt),
]);

export type PlivoCallbackEvent = typeof plivoCallbackEvents.$inferSelect;
